/**
 * NeoDocs Checkout - Iframe-based checkout overlay for Shopify
 * Handles Buy Now and Add to Cart checkout flows
 * Communicates with checkout.neodocs.in via postMessage
 */
(function () {
  // Configuration
  const CHECKOUT_DOMAIN_PROD = "checkout.neodocs.in";
  const CHECKOUT_DOMAIN_TEST = "checkoutneodocs1--v1-4-2-yew668w2.web.app";
  const CHECKOUT_DOMAIN = CHECKOUT_DOMAIN_PROD;
  const CHECKOUT_IFRAME_URL = `https://${CHECKOUT_DOMAIN}/checkout?source=buy-now`;
  const CHECKOUT_IFRAME_URL_CART = `https://${CHECKOUT_DOMAIN}/checkout?source=cart`;
  const CART_IFRAME_URL = `https://${CHECKOUT_DOMAIN}/cart`;

  // Backend endpoint that returns full order details for a given order token.
  // Usage: `${ORDER_STATUS_API}/${token}` → { success, order: { ...shopify_order_name, items, shipping_address, ... } }
  // This is the authoritative source for the real Shopify order number, items, address, payment status, etc.
  const ORDER_STATUS_API = "https://us-central1-neodocs-8d6cd.cloudfunctions.net/shopifyUser/order-by-token";

  const IFRAME_ID = "neodocs-checkout-iframe";
  const WRAPPER_ID = "neodocs-checkout-wrapper";
  const LOADING_ID = "neodocs-checkout-loading";
  const CHECKOUT_ORIGIN = `https://${CHECKOUT_DOMAIN}`;
  const PRELOAD_IFRAME_ID = "neodocs-preload-iframe";

  // Products available on marketplaces (matched to cart by SKU)
  const MARKETPLACE_PRODUCTS = [
    {
      name: "Home Insemination Kit",
      sku: "ND-HI-05",
      amazonUrl: "https://amzn.to/4uqoqnQ",
      emoji: "🧪",
      badge: "Amazon #1",
    },
    {
      name: "Alpha Supplement",
      sku: "Alpha-1",
      amazonUrl: "https://amzn.to/4n84UKl",
      emoji: "💊",
      badge: "Best Seller",
    },
    {
      name: "Postpartum Belt",
      sku: "ND-PB-01",
      amazonUrl: "https://amzn.to/48GtRH3",
      emoji: "🏋️",
      badge: "Top Rated",
    },
    {
      name: "Kidney Care",
      sku: "N_K-201",
      amazonUrl: "https://amzn.to/4cYBl9t",
      emoji: "🫘",
      badge: "Best Seller",
    },
  ];

  // Preloaded iframe reference
  let preloadedIframe = null;
  let isPreloading = false;

  // Single slot for the current modal's history-back cleanup fn
  let _modalPopstateCleanup = null;

  /**
   * Push a fake history entry so the mobile back button can be intercepted.
   * onBackPress is called when the user presses back.
   * Returns a cleanup fn to call when closing programmatically (not via back).
   */
  function pushModalToHistory(onBackPress) {
    history.pushState({ neodocsModal: true }, '');

    function handler() {
      window.removeEventListener('popstate', handler);
      _modalPopstateCleanup = null;
      onBackPress();
    }

    window.addEventListener('popstate', handler);

    _modalPopstateCleanup = function () {
      window.removeEventListener('popstate', handler);
      _modalPopstateCleanup = null;
      history.back(); // remove the state we pushed
    };
  }

  /** Call when closing a modal programmatically (not via back button). */
  function cleanupModalHistory() {
    if (_modalPopstateCleanup) _modalPopstateCleanup();
  }

  // Checkout state tracking for abandoned checkout detection
  let checkoutState = {
    isOpen: false,
    startTime: null,
    cartData: null,
    checkoutSuccessful: false
  };

  // Retry /cart.js once before giving up — Shopify's cart API can return a transient non-OK/non-JSON response (throttling) that a reload recovers.
  async function fetchCartWithRetry(retries = 1) {
    let lastErr;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const res = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
          cache: "no-store"
        });
        if (!res.ok) throw new Error(`/cart.js returned HTTP ${res.status}`);
        return await res.json();
      } catch (err) {
        lastErr = err;
        console.warn(`[NeoDocs] /cart.js fetch failed (attempt ${attempt + 1}/${retries + 1}):`, err);
        if (attempt < retries) await new Promise(r => setTimeout(r, 400));
      }
    }
    throw lastErr;
  }

  // Client-side correlation id — Buy Now no longer creates a real Shopify cart.
  function generateCheckoutToken() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "");
    return "nd" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  // Read normalized product data for Buy Now: the on-page JSON (snippets/neodocs-product-data.liquid) first, then Shopify's analytics meta as a fallback.
  function readBuyNowProduct(form) {
    const dataEl =
      (form && form.querySelector("script.neodocs-product-data")) ||
      ((form && form.closest("product-form, .shopify-section, section")) || document).querySelector("script.neodocs-product-data") ||
      document.querySelector("script.neodocs-product-data");

    if (dataEl) {
      try {
        const p = JSON.parse(dataEl.textContent);
        if (p && p.variants) return p;
      } catch (e) {
        console.warn("[NeoDocs] Could not parse neodocs-product-data:", e);
      }
    }

    // Fallback: Shopify injects this on every product page
    const meta = window.ShopifyAnalytics?.meta?.product;
    if (meta && meta.variants) {
      const ogImage = document.querySelector('meta[property="og:image"]')?.content || null;
      const ogTitle = document.querySelector('meta[property="og:title"]')?.content || null;
      return {
        productId: meta.id,
        title: ogTitle || meta.variants[0]?.name || "",
        url: window.location.pathname,
        featuredImage: ogImage,
        variants: meta.variants.map(v => ({
          id: v.id,
          sku: v.sku || "",
          price: v.price, // cents
          title: v.public_title || null,
          available: true,
          requiresShipping: true,
          grams: 0,
          featuredImage: ogImage
        }))
      };
    }

    return null;
  }

  // Build the Buy Now payload entirely from on-page product data — no AJAX cart call, which Cloudflare 403s.
  function buildBuyNowPayloadFromPage() {
    const form = document.querySelector('form[action$="/cart/add"]');
    if (!form) return { error: "Product form not found." };

    const fd = new FormData(form);
    const variantId = Number(fd.get("id"));
    const quantity = Number(fd.get("quantity")) || 1;
    if (!variantId) return { error: "No variant selected." };

    const product = readBuyNowProduct(form);
    if (!product) return { error: "Product data not found on page." };

    const variant = (product.variants || []).find(v => Number(v.id) === variantId);
    if (!variant) return { error: "Selected variant not found in product data." };

    const unitPrice = (variant.price || 0) / 100; // Shopify prices are in cents
    const linePrice = unitPrice * quantity;
    const variantTitle = variant.title && variant.title !== "Default Title" ? variant.title : null;
    const itemTitle = variantTitle ? `${product.title} - ${variantTitle}` : product.title;
    const currency = window.Shopify?.currency?.active || "INR";
    const productUrl = product.url || "";

    return {
      cartData: {
        token: generateCheckoutToken(),
        originalTotalPrice: linePrice,
        totalPrice: linePrice,
        totalDiscount: 0,
        itemsSubtotalPrice: linePrice,
        itemCount: quantity,
        currency: currency,
        requiresShipping: variant.requiresShipping !== false,
        discountCodes: [],
        cartLevelDiscountApplications: [],
        items: [{
          id: variantId,
          variantId: variantId,
          productId: product.productId,
          sku: variant.sku || "",
          title: itemTitle,
          price: unitPrice,
          finalPrice: unitPrice,
          linePrice: linePrice,
          quantity: quantity,
          totalDiscount: 0,
          discounts: [],
          image: variant.featuredImage || product.featuredImage || null,
          url: productUrl + (productUrl.indexOf("?") === -1 ? "?" : "&") + "variant=" + variantId,
          variant_title: variantTitle
        }],
        shopCurrency: currency,
        shopName: window.Shopify?.shop || null,
        shopDomain: window.location.hostname,
        timestamp: new Date().toISOString(),
        note: "",
        totalWeight: (variant.grams || 0) * quantity
      }
    };
  }

  /**
   * Prepare cart data for the Buy Now flow from on-page data (no cart API).
   */
  function fetchAndPrepareCartData(callback, buttonEl) {
    const result = buildBuyNowPayloadFromPage();

    if (result.error || !result.cartData) {
      console.error("[NeoDocs] Buy Now payload build failed:", result.error);
      updateLoadingError("Something went wrong. Please try again.", result.error);
      buttonEl && resetButton(buttonEl);
      return;
    }

    console.log("[NeoDocs] Buy Now payload (no cart API):", result.cartData);
    callback(result.cartData);
    buttonEl && resetButton(buttonEl);
  }

  /**
   * Fetch cart data for Cart Page checkout
   * Uses existing cart without clearing
   */
  async function fetchExistingCartData(callback, buttonEl) {
    try {
      const cart = await fetchCartWithRetry();

      if (cart.item_count === 0) {
        alert("Your cart is empty.");
        buttonEl && resetButton(buttonEl);
        closeCheckoutIframe();
        return;
      }

      const cartData = buildCartPayload(cart);
      console.log("[NeoDocs] Existing cart data ready:", cartData);
      callback(cartData, true); // true = cart flow
    } catch (err) {
      console.error("[NeoDocs] Failed to fetch cart:", err);
      updateLoadingError("Something went wrong. Please try again.", err);
      buttonEl && resetButton(buttonEl);
    }
  }

  /**
   * Build normalized cart payload for iframe
   */
  function buildCartPayload(cart) {
    return {
      // Critical tracking information
      token: cart.token,

      // Pricing information (convert from cents)
      originalTotalPrice: cart.original_total_price / 100,
      totalPrice: cart.total_price / 100,
      totalDiscount: cart.total_discount / 100,
      itemsSubtotalPrice: cart.items_subtotal_price / 100,

      // Cart metadata
      itemCount: cart.item_count,
      currency: cart.currency,
      requiresShipping: cart.requires_shipping,

      // Discount information
      discountCodes: cart.discount_codes || [],
      cartLevelDiscountApplications: cart.cart_level_discount_applications || [],

      // Items data
      items: cart.items.map(item => ({
        id: item.id,
        variantId: item.variant_id,
        productId: item.product_id,
        sku: item.sku,
        title: item.title,
        price: item.price / 100,
        finalPrice: item.final_price / 100,
        linePrice: item.line_price / 100,
        quantity: item.quantity,
        totalDiscount: item.total_discount / 100,
        discounts: item.discounts,
        image: item.image,
        url: item.url,
        variant_title: item.variant_title
      })),

      // Shop context
      shopCurrency: window.Shopify?.currency?.active || cart.currency,
      shopName: window.Shopify?.shop || null,
      shopDomain: window.location.hostname,

      // Timestamp
      timestamp: new Date().toISOString(),

      // Additional data
      note: cart.note || "",
      totalWeight: cart.total_weight || 0
    };
  }

  /**
   * Normalize cart token by removing shop prefix if present
   * @param {string} token - Cart token from Shopify
   * @returns {string} Normalized token
   */
  function normalizeCartToken(token) {
    if (!token) return null;
    // Remove any shop domain prefix if present (e.g., "shop.neodocs.in:" prefix)
    const parts = token.split(':');
    return parts.length > 1 ? parts[parts.length - 1] : token;
  }

  /**
   * Preload checkout iframe in background for faster loading
   */
  function preloadCheckoutIframe() {
    if (isPreloading || preloadedIframe) return;

    isPreloading = true;
    console.log('[NeoDocs] Preloading checkout iframe...');

    const iframe = document.createElement('iframe');
    iframe.id = PRELOAD_IFRAME_ID;
    iframe.src = CHECKOUT_IFRAME_URL;
    iframe.style.cssText = `
      position: fixed;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
      z-index: -1;
    `;
    iframe.setAttribute('loading', 'eager');

    iframe.onload = () => {
      console.log('[NeoDocs] Checkout iframe preloaded');
      preloadedIframe = iframe;
      isPreloading = false;
    };

    document.body.appendChild(iframe);
  }

  /**
   * Setup preloading on hover/focus of checkout buttons
   */
  function setupPreloading() {
    const preloadTriggers = [
      '.flo-buy-now-button',
      '#flo-buy-now-button',
      '.neodocs-buy-now',
      '.neodocs-cart-checkout',
      'button[name="checkout"]',
      '.cart__checkout-button'
    ];

    document.addEventListener('mouseover', (e) => {
      const target = e.target.closest(preloadTriggers.join(','));
      if (target) preloadCheckoutIframe();
    }, { passive: true });

    document.addEventListener('touchstart', (e) => {
      const target = e.target.closest(preloadTriggers.join(','));
      if (target) preloadCheckoutIframe();
    }, { passive: true });
  }

  /**
   * Create enhanced loading animation with progress steps
   * @param {string} type - 'checkout' or 'cart'
   */
  function createLoadingAnimation(type = 'checkout') {
    const loadingDiv = document.createElement('div');
    loadingDiv.id = LOADING_ID;

    const messages = type === 'cart'
      ? ['Loading your cart...', 'Fetching items...', 'Almost ready...']
      : ['Preparing your order...', 'Setting up checkout...', 'Almost ready...'];

    const trustLogos = window.NEODOCS_TRUST_LOGOS || {};
    const neodocsLogo = trustLogos.neodocs || '';
    const isoLogo = trustLogos.iso13485 || '';
    const icmrLogo = trustLogos.icmr || '';
    const cdscoLogo = trustLogos.cdsco || '';
    const doctorsLogo = trustLogos.doctors || '';

    const isoLogoHtml = `
      ${isoLogo ? `<img class="neodocs-trust-logo" src="${isoLogo}" alt="ISO 13485 Certified" loading="eager" decoding="async" onerror="this.closest('.neodocs-trust-item').classList.remove('has-logo'); this.style.display='none';">` : ''}
      <span class="neodocs-trust-badge">ISO 13485</span>
    `;
    const icmrLogoHtml = `
      ${icmrLogo ? `<img class="neodocs-trust-logo" src="${icmrLogo}" alt="Supported by ICMR" loading="eager" decoding="async" onerror="this.closest('.neodocs-trust-item').classList.remove('has-logo'); this.style.display='none';">` : ''}
      <span class="neodocs-trust-badge">ICMR</span>
    `;
    const cdscoLogoHtml = `
      ${cdscoLogo ? `<img class="neodocs-trust-logo" src="${cdscoLogo}" alt="CDSCO" loading="eager" decoding="async" onerror="this.closest('.neodocs-trust-item').classList.remove('has-logo'); this.style.display='none';">` : ''}
      <span class="neodocs-trust-badge">CDSCO</span>
    `;

    loadingDiv.innerHTML = `
      <div class="neodocs-loading-shell">
        <div class="neodocs-loading-header">
         <div class="neodocs-loading-brand">
            <div class="neodocs-loading-brand">
              <div class="neodocs-logo-box">
                  <div class="neodocs-circle"></div>
                  ${neodocsLogo ? `<img src="${neodocsLogo}" alt="Neodocs" class="neodocs-logo-image">` : ''}
              </div>
            <span class="neodocs-brand-name">Neodocs</span>
        </div>
          <p class="neodocs-loading-tagline">Supporting your parenthood journey</p>
        </div>

        <div class="neodocs-loading-core">
          <div class="neodocs-loading-icon">
            <svg width="64" height="64" viewBox="0 0 60 60" aria-hidden="true">
              <circle class="neodocs-loading-circle-bg" cx="30" cy="30" r="26" fill="none" stroke="#eef2f5" stroke-width="4"/>
              <circle class="neodocs-loading-circle" cx="30" cy="30" r="26" fill="none" stroke="#2f7d6b" stroke-width="4" stroke-linecap="round"/>
              <path class="neodocs-loading-check" d="M20 30 L27 37 L40 24" fill="none" stroke="#2f7d6b" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>

          <div class="neodocs-progress-steps">
            <div class="neodocs-step active" data-step="1">
              <span class="neodocs-step-dot"></span>
            </div>
            <div class="neodocs-step-line"></div>
            <div class="neodocs-step" data-step="2">
              <span class="neodocs-step-dot"></span>
            </div>
            <div class="neodocs-step-line"></div>
            <div class="neodocs-step" data-step="3">
              <span class="neodocs-step-dot"></span>
            </div>
          </div>

          <p class="neodocs-loading-message">${messages[0]}</p>
          <p class="neodocs-loading-subtext">Secure and private checkout</p>

          <div class="neodocs-skeleton-preview">
            <div class="neodocs-skeleton-header"></div>
            <div class="neodocs-skeleton-item">
              <div class="neodocs-skeleton-image"></div>
              <div class="neodocs-skeleton-text">
                <div class="neodocs-skeleton-line"></div>
                <div class="neodocs-skeleton-line short"></div>
              </div>
            </div>
            <div class="neodocs-skeleton-button"></div>
          </div>
        </div>

        <div class="neodocs-trust-row">
          <div class="neodocs-trust-item ${isoLogo ? 'has-logo' : ''}">
            ${isoLogoHtml}
            <span>ISO 13485</span>
          </div>
          <div class="neodocs-trust-item ${icmrLogo ? 'has-logo' : ''}">
            ${icmrLogoHtml}
            <span>ICMR</span>
          </div>
          <div class="neodocs-trust-item ${cdscoLogo ? 'has-logo' : ''}">
            ${cdscoLogoHtml}
            <span>CDSCO</span>
          </div>
        </div>
        
        <div class="neodocs-trust-fixed ${doctorsLogo ? 'has-logo' : ''}">
          <span>Recognized by 30,000+ Doctors</span>
        </div>
      </div>
    `;

    loadingDiv.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(180deg, #ffffff 0%, #f6faf9 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      border-radius: inherit;
    `;

    // Add CSS for enhanced loading animation (only once)
    if (!document.getElementById('neodocs-checkout-styles')) {
      const style = document.createElement('style');
      style.id = 'neodocs-checkout-styles';
      style.textContent = `
        .neodocs-loading-shell {
          text-align: center;
          color: #21312c;
          padding: 24px 20px;
          width: 100%;
          max-width: 360px;
          display: grid;
          gap: 18px;
        }

        .neodocs-loading-header {
          display: grid;
          gap: 6px;
        }

        .neodocs-loading-brand {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          font-weight: 700;
          letter-spacing: 0.2px;
          font-size: 16px;
        }

        .neodocs-loading-tagline {
          margin: 0px 0 6px;
          font-size: 14px;
          font-weight: 300;
          color: #4b5b57;
        }

        /* Animated circular loader */
        .neodocs-loading-core {
          display: grid;
          gap: 12px;
          align-items: center;
          justify-items: center;
        }

        .neodocs-loading-icon {
          margin-bottom: 6px;
        }

        .neodocs-loading-circle-bg {
          opacity: 0.3;
        }

        .neodocs-loading-circle {
          stroke-dasharray: 163;
          stroke-dashoffset: 163;
          animation: neodocs-circle-fill 2s ease-out forwards;
          transform-origin: center;
          transform: rotate(-90deg);
        }

        .neodocs-loading-check {
          stroke-dasharray: 50;
          stroke-dashoffset: 50;
          opacity: 0;
        }

        @keyframes neodocs-circle-fill {
          0% { stroke-dashoffset: 163; }
          100% { stroke-dashoffset: 0; }
        }

        @keyframes neodocs-check-draw {
          0% { stroke-dashoffset: 50; opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }

        /* Progress steps */
        .neodocs-progress-steps {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 6px;
          gap: 0;
        }

        .neodocs-step {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .neodocs-step-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #e0e0e0;
          transition: all 0.3s ease;
        }
        .neodocs-loading-brand{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
}

.neodocs-loading-brand{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
}

/* Green square */
.neodocs-logo-box{
    width: 80px;
    height: 80px;
    background: #22a06b;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

/* White outline circle */
.neodocs-circle{
    width: 42px;
    height: 42px;
    border: 3px solid white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

/* Asset image */
.neodocs-circle img{
    width: 22px;
    height: 22px;
    object-fit: contain;
}

/* Neodocs logo */
.neodocs-logo-image{
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
}

/* Brand text */
.neodocs-brand-name{
    font-size: 24px;
    font-weight: 800;
    color: #111827;
    letter-spacing: -1px;
    font-family: Arial, sans-serif;
}

        .neodocs-step.active .neodocs-step-dot {
          background: #2f7d6b;
          transform: scale(1.2);
          box-shadow: 0 0 0 4px rgba(47, 125, 107, 0.18);
        }

        .neodocs-step.completed .neodocs-step-dot {
          background: #22a06b;
        }

        .neodocs-step-line {
          width: 30px;
          height: 2px;
          background: #e0e0e0;
          margin: 0 4px;
          transition: background 0.3s ease;
        }

        .neodocs-step-line.active {
          background: linear-gradient(90deg, #2f7d6b 0%, #e0e0e0 100%);
          animation: neodocs-line-fill 0.5s ease forwards;
        }

        .neodocs-step-line.completed {
          background: #22a06b;
        }

        @keyframes neodocs-line-fill {
          0% { background: linear-gradient(90deg, #007bff 0%, #e0e0e0 0%); }
          100% { background: linear-gradient(90deg, #007bff 100%, #e0e0e0 100%); }
        }

        /* Loading message */
        .neodocs-loading-message {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2f2a;
          min-height: 24px;
          animation: neodocs-fade-in 0.3s ease;
        }

        .neodocs-loading-subtext {
          margin: 0 0 6px;
          font-size: 13px;
          color: #5b6a66;
        }

        @keyframes neodocs-fade-in {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Skeleton loading preview */
        .neodocs-skeleton-preview {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          width: 100%;
        }

        .neodocs-skeleton-header {
          height: 20px;
          background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
          background-size: 200% 100%;
          animation: neodocs-shimmer 1.5s infinite;
          border-radius: 4px;
          margin-bottom: 16px;
          width: 60%;
        }

        .neodocs-skeleton-item {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }

        .neodocs-skeleton-image {
          width: 50px;
          height: 50px;
          background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
          background-size: 200% 100%;
          animation: neodocs-shimmer 1.5s infinite;
          border-radius: 8px;
          flex-shrink: 0;
        }

        .neodocs-skeleton-text {
          flex: 1;
        }

        .neodocs-skeleton-line {
          height: 14px;
          background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
          background-size: 200% 100%;
          animation: neodocs-shimmer 1.5s infinite;
          border-radius: 4px;
          margin-bottom: 8px;
        }

        .neodocs-skeleton-line.short {
          width: 60%;
        }

        .neodocs-skeleton-button {
          height: 44px;
          background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
          background-size: 200% 100%;
          animation: neodocs-shimmer 1.5s infinite;
          border-radius: 8px;
        }

        @keyframes neodocs-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .neodocs-trust-row {
          position: relative;
          height: 36px;
          text-align: center;
          margin: 6px auto 0;
          padding: 0 16px;
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid #edf2f0;
          box-shadow: 0 8px 18px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          width: 200px;
        }

        .neodocs-trust-item {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 13px;
          color: #2c3b36;
          font-weight: 600;
          opacity: 0;
          transform: translateY(6px);
          animation: neodocs-trust-cycle 9s infinite;
        }

        .neodocs-trust-item:nth-child(1) { animation-delay: 0s; }
        .neodocs-trust-item:nth-child(2) { animation-delay: 3s; }
        .neodocs-trust-item:nth-child(3) { animation-delay: 6s; }

        .neodocs-trust-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 10px;
        }

        .neodocs-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #d7e3de;
          animation: neodocs-dot-cycle 9s infinite;
        }

        .neodocs-dot:nth-child(1) { animation-delay: 0s; }
        .neodocs-dot:nth-child(2) { animation-delay: 3s; }
        .neodocs-dot:nth-child(3) { animation-delay: 6s; }

        .neodocs-trust-fixed {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: #2c3b36;
          font-weight: 600;
          justify-content: center;
          margin-top: 8px;
        }

        .neodocs-trust-logo {
          height: 22px;
          width: auto;
          display: block;
        }

        .neodocs-trust-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 22px;
          padding: 0 8px;
          border-radius: 999px;
          background: #eef6f3;
          color: #2f7d6b;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.2px;
        }

        .neodocs-trust-item.has-logo .neodocs-trust-badge {
          display: none;
        }

        @keyframes neodocs-trust-cycle {
          0% { opacity: 0; transform: translateY(6px); }
          8% { opacity: 1; transform: translateY(0); }
          25% { opacity: 1; transform: translateY(0); }
          33% { opacity: 0; transform: translateY(-6px); }
          100% { opacity: 0; transform: translateY(-6px); }
        }

        @keyframes neodocs-dot-cycle {
          0% { background: #2f7d6b; transform: scale(1.15); box-shadow: 0 0 0 4px rgba(47, 125, 107, 0.2); }
          25% { background: #2f7d6b; transform: scale(1.15); box-shadow: 0 0 0 4px rgba(47, 125, 107, 0.2); }
          33% { background: #d7e3de; transform: scale(1); box-shadow: none; }
          100% { background: #d7e3de; transform: scale(1); box-shadow: none; }
        }

        @keyframes neodocs-pulse {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 0.8; }
        }

        /* Full screen on mobile, modal on desktop */
        @media (max-width: 768px) {
          #${WRAPPER_ID} {
            align-items: flex-start !important;
            padding: 0 !important;
          }

          #${WRAPPER_ID} > div {
            width: 100% !important;
            max-width: 100% !important;
            height: 100vh !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
          }

          #${IFRAME_ID} {
            border-radius: 0 !important;
          }

          .neodocs-loading-shell {
            max-width: 100%;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Start progress animation
    startProgressAnimation(loadingDiv, messages);

    return loadingDiv;
  }

  /**
   * Animate progress steps and messages
   */
  function startProgressAnimation(loadingDiv, messages) {
    const steps = loadingDiv.querySelectorAll('.neodocs-step');
    const lines = loadingDiv.querySelectorAll('.neodocs-step-line');
    const messageEl = loadingDiv.querySelector('.neodocs-loading-message');
    const circle = loadingDiv.querySelector('.neodocs-loading-circle');
    const check = loadingDiv.querySelector('.neodocs-loading-check');

    let currentStep = 0;

    const resetSteps = () => {
      steps.forEach(step => {
        step.classList.remove('completed');
        step.classList.remove('active');
      });
      lines.forEach(line => {
        line.classList.remove('completed');
        line.classList.remove('active');
      });
      if (circle) {
        circle.style.stroke = '#2f7d6b';
        circle.style.strokeDashoffset = '163';
        circle.style.animation = 'none';
        circle.offsetHeight;
        circle.style.animation = 'neodocs-circle-fill 2s ease-out forwards';
      }
      if (check) {
        check.style.animation = 'none';
        check.style.opacity = '0';
      }
      currentStep = 0;
      if (steps[0]) steps[0].classList.add('active');
    };

    const advanceStep = () => {
      if (currentStep < steps.length - 1) {
        steps[currentStep].classList.remove('active');
        steps[currentStep].classList.add('completed');
        if (lines[currentStep]) {
          lines[currentStep].classList.add('completed');
        }
        currentStep++;
        steps[currentStep].classList.add('active');
        if (messageEl && messages[currentStep]) {
          messageEl.style.animation = 'none';
          messageEl.offsetHeight;
          messageEl.style.animation = 'neodocs-fade-in 0.3s ease';
          messageEl.textContent = messages[currentStep];
        }
      } else {
        // Loop instead of completing
        resetSteps();
        if (messageEl && messages[0]) {
          messageEl.textContent = messages[0];
        }
      }
    };

    resetSteps();
    loadingDiv.progressInterval = setInterval(advanceStep, 600);
  }

  /**
   * Show checkout iframe modal
   */
  function showCheckoutIframe() {
    if (document.getElementById(WRAPPER_ID)) return;

    // Track checkout state for abandoned checkout detection
    checkoutState = {
      isOpen: true,
      startTime: Date.now(),
      cartData: null,
      checkoutSuccessful: false
    };

    const wrapper = document.createElement('div');
    wrapper.id = WRAPPER_ID;
    wrapper.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.5);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;

    const iframeContainer = document.createElement('div');
    iframeContainer.style.cssText = `
      position: relative;
      width: 100%;
      max-width: 500px;
      height: 90vh;
      max-height: 700px;
      background: white;
      border-radius: 16px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.3);
      overflow: hidden;
    `;

    // Add enhanced loading animation
    const loadingDiv = createLoadingAnimation('checkout');
    iframeContainer.appendChild(loadingDiv);

    // Close on backdrop click
    wrapper.addEventListener('click', function (e) {
      if (e.target === wrapper) {
        closeCheckoutIframe();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', handleEscapeKey);

    wrapper.appendChild(iframeContainer);
    document.body.appendChild(wrapper);
    document.body.style.overflow = 'hidden';

    // Intercept mobile back button
    pushModalToHistory(() => closeCheckoutIframe());

    // Fade in
    requestAnimationFrame(() => {
      wrapper.style.opacity = '1';
    });

    return { wrapper, iframeContainer, loadingDiv };
  }

  function handleEscapeKey(e) {
    if (e.key === 'Escape') {
      closeCheckoutIframe();
    }
  }

  /**
   * Load checkout iframe with cart data
   */
  function loadCheckoutWithCartData(cartData, isCartFlow = false) {
    const existingWrapper = document.getElementById(WRAPPER_ID);
    if (!existingWrapper) {
      console.error("[NeoDocs] Wrapper not found");
      return;
    }

    const iframeContainer = existingWrapper.querySelector('div');
    const loadingDiv = document.getElementById(LOADING_ID);

    if (!iframeContainer) {
      console.error("[NeoDocs] Iframe container not found");
      return;
    }

    const iframeUrl = isCartFlow ? CHECKOUT_IFRAME_URL_CART : CHECKOUT_IFRAME_URL;
    console.log("[NeoDocs] Loading checkout with cart data");

    // Store cart data for abandoned checkout tracking
    checkoutState.cartData = cartData;

    // Check if we have a preloaded iframe we can reuse
    let iframe;
    const preloaded = document.getElementById(PRELOAD_IFRAME_ID);

    if (preloaded && !isCartFlow) {
      // Reuse preloaded iframe
      console.log("[NeoDocs] Using preloaded iframe");
      iframe = preloaded;
      iframe.id = IFRAME_ID;
      iframe.style.cssText = `
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 16px;
        background: white;
        opacity: 0;
        transition: opacity 0.2s ease;
      `;
      iframe.setAttribute('loading', 'eager');
      iframe.setAttribute('fetchpriority', 'high');
      iframeContainer.appendChild(iframe);
      preloadedIframe = null;
    } else {
      // Create new iframe
      iframe = document.createElement("iframe");
      iframe.src = iframeUrl;
      iframe.id = IFRAME_ID;
      iframe.style.cssText = `
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 16px;
        background: white;
        opacity: 0;
        transition: opacity 0.2s ease;
      `;
      iframe.setAttribute('loading', 'eager');
      iframe.setAttribute('fetchpriority', 'high');
      iframe.allow = "payment";
      iframeContainer.appendChild(iframe);
    }

    let cartDataSent = false;
    let iframeReady = false;

    // Function to send cart data to iframe
    const sendCartData = () => {
      if (!cartDataSent && iframe.contentWindow) {
        try {
          console.log("[NeoDocs] Sending cart data to iframe:", cartData);
          iframe.contentWindow.postMessage({
            type: 'SHOPIFY_CART_DATA',
            cart: cartData,
            cookies: document.cookie
          }, CHECKOUT_ORIGIN);
          cartDataSent = true;
        } catch (error) {
          console.error("[NeoDocs] Error sending cart data:", error);
        }
      }
    };

    // Optimistic early send (in case iframe listener is already ready)


    // Function to show iframe (called when ready)
    const showIframe = () => {
      if (iframeReady) return;
      iframeReady = true;

      // Clear any progress animation
      if (loadingDiv && loadingDiv.progressInterval) {
        clearInterval(loadingDiv.progressInterval);
      }

      // Smooth transition to iframe
      if (loadingDiv) {
        loadingDiv.style.opacity = '0';
        loadingDiv.style.transition = 'opacity 0.2s ease';
      }
      iframe.style.opacity = '1';

      setTimeout(() => {
        if (loadingDiv && loadingDiv.parentNode) {
          loadingDiv.remove();
        }
      }, 200);
    };

    // Handle iframe load
    iframe.onload = () => {
      console.log("[NeoDocs] Iframe loaded");

      setTimeout(() => {
        if (!cartDataSent) {
          console.log("[NeoDocs] Fallback: sending cart data after timeout");
          sendCartData();
        }
      }, 500);

      setTimeout(() => {
        if (!iframeReady) {
          showIframe();
        }
      }, 300);
    };

    // Message handler for iframe communication
    const handleIframeMessage = (event) => {
      if (event.origin !== CHECKOUT_ORIGIN) return;

      console.log("[NeoDocs] Received message from iframe:", event.data?.type);

      switch (event.data?.type) {
        case 'CHECKOUT_READY':
          console.log("[NeoDocs] Checkout iframe is ready, sending cart data");
          sendCartData();
          setTimeout(showIframe, 100);
          break;

        case 'CHECKOUT_LOGIN_SUCCESS':
          console.log("[NeoDocs] User logged in successfully");
          break;

        case 'CHECKOUT_PAYMENT_SELECTED':
          console.log("[NeoDocs] Payment method selected:", event.data.paymentData?.method);
          if (event.data.paymentData?.method === 'cod') {
            handleCODPayment(event.data.paymentData);
          }
          break;

        case 'FLO_CHECKOUT_CLOSE':
          if (event.data.statusType === 'success') {
            checkoutState.checkoutSuccessful = true;
            handleCheckoutSuccess(event.data);
          } else if (event.data.statusType === 'abandon') {
            // Explicit abandon from iframe - just redirect, no modal
            handleExplicitAbandon();
          } else {
            // Back button case (statusType is empty/undefined) - show feedback modal
            handleAbandonedCheckout('back_button', event.data);
          }
          break;

        case 'FLO_CHECKOUT_ERROR':
          updateLoadingError(event.data.message || "Checkout error occurred.");
          break;

        case 'CHECKOUT_RESIZE':
          // Handle iframe resize if needed
          break;
      }
    };

    window.addEventListener('message', handleIframeMessage);
    iframe.messageHandler = handleIframeMessage;
  }

  /**
   * Handle COD payment completion
   */
  function handleCODPayment(paymentData) {
    console.log("[NeoDocs] Processing COD payment, cartData:", checkoutState.cartData);
    const cartData = checkoutState.cartData;
    closeCheckoutIframe(true);
    setTimeout(() => showAmazonFlipkartModal(cartData), 300);
  }

  /**
   * Handle successful checkout completion
   */
  function handleCheckoutSuccess(checkoutData) {
    console.log("[NeoDocs] Checkout successful:", checkoutData);

    const cartToken = checkoutData.cartToken;

    // Mark as successful before closing
    checkoutState.checkoutSuccessful = true;
    closeCheckoutIframe(true); // true = skip abandoned checkout handling

    // Save order details
    if (cartToken) {
      localStorage.setItem('neodocs_last_order_token', cartToken);
    }

    if (checkoutData.orderDetails) {
      localStorage.setItem('neodocs_order_details', JSON.stringify(checkoutData.orderDetails));
    }

    if (checkoutData.userAddress) {
      localStorage.setItem('neodocs_user_address', JSON.stringify(checkoutData.userAddress));
    }

    // Redirect to order status page
    if (cartToken) {
      window.location.href = `/pages/order-status?token=${encodeURIComponent(cartToken)}`;
    } else {
      window.location.href = '/pages/order-status';
    }
  }

  /**
   * Handle explicit abandon from iframe (statusType === 'abandon')
   * Just close iframe and redirect to product page - no feedback modal
   */
  function handleExplicitAbandon() {
    console.log("[NeoDocs] Explicit abandon - redirecting to product page");

    // Close the iframe
    closeCheckoutIframe(true); // true = skip abandoned checkout handling

    // Redirect to the current product page or home page
    const productUrl = window.location.pathname;
    if (productUrl && productUrl !== '/') {
      window.location.href = productUrl;
    } else {
      window.location.reload();
    }
  }

  /**
   * Handle abandoned checkout - show recovery page
   * @param {string} reason - Why checkout was abandoned (backdrop_click, escape_key, iframe_close, back_button)
   * @param {object} checkoutData - Any data from iframe (may be empty)
   */
  function handleAbandonedCheckout(reason, checkoutData = {}) {
    console.log("[NeoDocs] Checkout abandoned:", reason, checkoutData);

    // Don't handle if checkout was successful
    if (checkoutState.checkoutSuccessful) return;

    // Calculate time spent in checkout
    const timeSpent = checkoutState.startTime ? Math.round((Date.now() - checkoutState.startTime) / 1000) : 0;

    // Save abandoned checkout data for recovery
    // checkoutData.cartData is used when called from closeCheckoutIframe (state already reset)
    // checkoutState.cartData is used when called directly (back_button from iframe)
    const abandonedData = {
      reason: reason,
      timeSpent: timeSpent,
      cartData: checkoutData.cartData || checkoutState.cartData,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href
    };

    localStorage.setItem('neodocs_abandoned_checkout', JSON.stringify(abandonedData));

    // Close the iframe
    closeCheckoutIframe(true); // true = skip recursive abandoned handling

    // Show abandoned checkout modal/page
    showAbandonedCheckoutModal(abandonedData);
  }

  /**
   * Show abandoned checkout recovery modal
   */
  function showAbandonedCheckoutModal(abandonedData) {
    // Remove any existing modal
    const existingModal = document.getElementById('neodocs-abandoned-modal');
    if (existingModal) existingModal.remove();

    const modal = document.createElement('div');
    modal.id = 'neodocs-abandoned-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.6);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;

    modal.innerHTML = `
      <div style="
        background: white;
        border-radius: 16px;
        max-width: 420px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        animation: neodocs-slide-up 0.3s ease;
      ">
        <!-- Header -->
        <div style="
          padding: 24px 24px 16px;
          text-align: center;
          border-bottom: 1px solid #f0f0f0;
        ">
          <div style="
            width: 64px;
            height: 64px;
            background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 16px;
          ">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f57c00" stroke-width="2">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          </div>
          <h2 style="margin: 0 0 8px; font-size: 22px; font-weight: 600; color: #1a1a1a;">
            Wait! Don't leave yet
          </h2>
          <p style="margin: 0; color: #666; font-size: 14px;">
            Your items are waiting for you
          </p>
        </div>

        <!-- Feedback Section -->
        <div style="padding: 20px 24px;">
          <p style="margin: 0 0 16px; font-size: 14px; color: #666; text-align: center;">
            Help us improve - what stopped you?
          </p>

          <div id="neodocs-feedback-options" style="display: flex; flex-direction: column; gap: 10px;">
            <button class="neodocs-feedback-btn" data-reason="price" style="
              padding: 12px 16px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background: white;
              cursor: pointer;
              text-align: left;
              font-size: 14px;
              color: #333;
              transition: all 0.2s ease;
            ">
              💰 Price is too high
            </button>
            <button class="neodocs-feedback-btn" data-reason="shipping" style="
              padding: 12px 16px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background: white;
              cursor: pointer;
              text-align: left;
              font-size: 14px;
              color: #333;
              transition: all 0.2s ease;
            ">
              🚚 Shipping concerns
            </button>
            <button class="neodocs-feedback-btn" data-reason="payment" style="
              padding: 12px 16px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background: white;
              cursor: pointer;
              text-align: left;
              font-size: 14px;
              color: #333;
              transition: all 0.2s ease;
            ">
              💳 Payment issues
            </button>
            <button class="neodocs-feedback-btn" data-reason="browsing" style="
              padding: 12px 16px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background: white;
              cursor: pointer;
              text-align: left;
              font-size: 14px;
              color: #333;
              transition: all 0.2s ease;
            ">
              👀 Just browsing
            </button>
            <button class="neodocs-feedback-btn" data-reason="cod" style="
              padding: 12px 16px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background: white;
              cursor: pointer;
              text-align: left;
              font-size: 14px;
              color: #333;
              transition: all 0.2s ease;
            ">
              💵 Looking for COD option
            </button>
            <button class="neodocs-feedback-btn" data-reason="other" style="
              padding: 12px 16px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background: white;
              cursor: pointer;
              text-align: left;
              font-size: 14px;
              color: #333;
              transition: all 0.2s ease;
            ">
              🤔 Other reason
            </button>
          </div>
        </div>

        <!-- Action Buttons -->
        <div style="padding: 16px 24px 24px; display: flex; flex-direction: column; gap: 12px;">
          <button id="neodocs-resume-checkout" style="
            width: 100%;
            padding: 14px 24px;
            background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          ">
            Complete My Purchase
          </button>
          <button id="neodocs-close-abandoned" style="
            width: 100%;
            padding: 12px 24px;
            background: transparent;
            color: #666;
            border: none;
            font-size: 14px;
            cursor: pointer;
          ">
            Maybe later
          </button>
        </div>
      </div>

      <style>
        @keyframes neodocs-slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .neodocs-feedback-btn:hover {
          border-color: #007bff !important;
          background: #f8f9ff !important;
        }
        .neodocs-feedback-btn.selected {
          border-color: #007bff !important;
          background: #e7f1ff !important;
        }
        #neodocs-resume-checkout:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
        }
        @media (max-width: 480px) {
          #neodocs-abandoned-modal > div {
            margin: 10px;
            max-height: calc(100vh - 20px);
          }
        }
      </style>
    `;

    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    // Fade in
    requestAnimationFrame(() => {
      modal.style.opacity = '1';
    });

    // Bind event handlers
    const dismissFn = bindAbandonedModalEvents(modal, abandonedData);

    // Intercept mobile back button
    pushModalToHistory(dismissFn);
  }

  /**
   * Bind events for abandoned checkout modal
   */
  function bindAbandonedModalEvents(modal, abandonedData) {
    const cartData = abandonedData.cartData;

    const dismissAndShowMarketplace = () => {
      console.log('[NeoDocs] dismissAndShowMarketplace, cartData:', cartData);
      closeAbandonedModal();
      setTimeout(() => showAmazonFlipkartModal(cartData), 300);
    };

    // Feedback buttons — track reason, close modal, show marketplace modal
    const feedbackBtns = modal.querySelectorAll('.neodocs-feedback-btn');
    feedbackBtns.forEach(btn => {
      btn.addEventListener('click', function () {
        const reason = this.getAttribute('data-reason');
        trackAbandonedFeedback(reason, abandonedData);
        dismissAndShowMarketplace();
      });
    });

    // Resume checkout — close modal only, re-open checkout
    const resumeBtn = modal.querySelector('#neodocs-resume-checkout');
    resumeBtn.addEventListener('click', () => {
      closeAbandonedModal();
      setTimeout(() => handleCartCheckout({}), 300);
    });

    // "Maybe later" — close modal, show marketplace modal
    const closeBtn = modal.querySelector('#neodocs-close-abandoned');
    closeBtn.addEventListener('click', dismissAndShowMarketplace);

    // Backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) dismissAndShowMarketplace();
    });

    // Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        dismissAndShowMarketplace();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    return dismissAndShowMarketplace;
  }

  /**
   * Track abandoned checkout feedback - sends to new Firebase endpoint
   */
  function trackAbandonedFeedback(reason, abandonedData) {
    console.log('[NeoDocs] Abandoned feedback:', reason);

    // Get cart data from abandonedData or checkoutState
    const cartData = abandonedData.cartData || checkoutState.cartData || {};

    // Get phone number from stored user data
    let phoneNumber = null;
    try {
      const storedUserData = localStorage.getItem('neodocs_user_data');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        phoneNumber = userData.phone || null;
      }
    } catch (e) {
      // Silently fail
    }

    // Send to Firebase backend
    try {
      fetch('https://us-central1-neodocs-8d6cd.cloudfunctions.net/shopifyUser/abandon-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cartToken: cartData.token,
          normalizedToken: normalizeCartToken(cartData.token),
          phone: phoneNumber,
          reason: reason,
          exitStep: checkoutState.currentStep || null,
          shopDomain: window.location.hostname,
          line_items: (cartData.items || []).map(item => ({
            title: item.title,
            quantity: item.quantity,
            price: item.price
          }))
        })
      }).catch(() => { }); // Silently fail
    } catch (e) {
      // Silently fail
    }
  }

  /**
   * Show "Before you go" / marketplace modal.
   * Tries to match cart items by SKU; falls back to full catalog if none match.
   * Uses cart item image → emoji as fallback visual.
   */
  function showAmazonFlipkartModal(cartData) {
    console.log('[NeoDocs] showAmazonFlipkartModal called, cartData:', cartData);

    const existingModal = document.getElementById('neodocs-marketplace-modal');
    if (existingModal) existingModal.remove();

    const logos = window.NEODOCS_MARKETPLACE_LOGOS || {};
    const amazonLogo = logos.amazon || '';
    const flipkartLogo = logos.flipkart || '';

    // Only show products whose SKU exists in the cart (case-insensitive, trimmed)
    const cartItems = (cartData && cartData.items) || [];
    console.log('[NeoDocs] Cart items for marketplace match:', cartItems.map(i => ({ sku: i.sku, title: i.title })));

    const matchedProducts = [];
    cartItems.forEach(cartItem => {
      const cartSku = (cartItem.sku || '').trim().toLowerCase();
      const entry = MARKETPLACE_PRODUCTS.find(p => {
        const pSku = (p.sku || '').trim().toLowerCase();
        return pSku && cartSku && pSku === cartSku;
      });
      if (entry && !matchedProducts.find(m => m.sku === entry.sku)) {
        matchedProducts.push({ ...entry, cartImage: cartItem.image || null });
      }
    });

    console.log('[NeoDocs] SKU matched products:', matchedProducts.length);

    // No SKU match — don't show the modal
    if (matchedProducts.length === 0) return;

    const amazonLogoHtml = amazonLogo
      ? `<img src="${amazonLogo}" alt="Amazon" style="height:30px;width:auto;vertical-align:middle;">`
      : `<span style="font-weight:800;font-size:17px;color:#232f3e;letter-spacing:-0.5px;">amazon</span>`;

    const flipkartLogoHtml = flipkartLogo
      ? `<img src="${flipkartLogo}" alt="Flipkart" style="height:20px;width:auto;vertical-align:middle;">`
      : `<span style="font-weight:800;font-size:15px;color:#2874F0;">flipkart</span>`;

    const productRows = matchedProducts.map(p => {
      const visual = p.cartImage
        ? `<img src="${p.cartImage}" alt="${p.name}" style="width:56px;height:56px;object-fit:cover;border-radius:10px;flex-shrink:0;">`
        : `<div style="width:56px;height:56px;border-radius:10px;background:#f5f5f5;display:flex;align-items:center;justify-content:center;font-size:28px;flex-shrink:0;">${p.emoji || '📦'}</div>`;

      const badgeHtml = p.badge
        ? `<span style="display:inline-flex;align-items:center;padding:4px 12px;border:1.5px solid #f0a500;border-radius:999px;font-size:12px;font-weight:600;color:#c47f00;white-space:nowrap;">${p.badge}</span>`
        : '';

      const dest = p.amazonUrl || p.flipkartUrl || '#';

      return `
        <a href="${dest}" target="_blank" rel="noopener" style="
          display:flex;align-items:center;gap:14px;
          background:#f7f7f7;border-radius:12px;
          padding:12px 14px;text-decoration:none;
          margin-bottom:10px;
        ">
          ${visual}
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;font-size:15px;color:#1a1a1a;margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${p.name}</div>
            ${p.sku ? `<div style="font-size:12px;color:#999;">${p.sku}</div>` : ''}
          </div>
          <div style="display:flex;align-items:center;gap:8px;flex-shrink:0;">
            ${badgeHtml}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#bbb" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </a>
      `;
    }).join('');

    const modal = document.createElement('div');
    modal.id = 'neodocs-marketplace-modal';
    modal.style.cssText = `
      position:fixed;top:0;left:0;right:0;bottom:0;
      background:rgba(0,0,0,0.5);
      z-index:999999;
      display:flex;align-items:center;justify-content:center;
      padding:20px;
      opacity:0;transition:opacity 0.3s ease;
    `;

    modal.innerHTML = `
      <div style="
        background:white;
        border-radius:20px;
        width:100%;max-width:420px;
        max-height:90vh;overflow-y:auto;
        box-shadow:0 20px 60px rgba(0,0,0,0.25);
        animation:neodocs-slide-up 0.3s ease;
      ">
        <!-- Header -->
        <div style="display:flex;align-items:flex-start;justify-content:space-between;padding:22px 22px 10px;">
          
           <div style="display:flex;flex-direction:column;gap:8px;">
            <span style="font-size:16px;font-weight:600;color:#1a1a1a;">COD available on</span>
            <div style="display:flex;align-items:center;gap:14px;">
              ${amazonLogoHtml}
              ${flipkartLogoHtml}
            </div>
          </div>
          <button id="neodocs-marketplace-close" style="
            background:#f0f0f0;border:none;border-radius:50%;
            width:32px;height:32px;display:flex;align-items:center;justify-content:center;
            cursor:pointer;flex-shrink:0;margin-left:12px;
          " aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

       

        <!-- Products -->
        <div style="padding:0 20px 8px;">
          ${productRows}
        </div>

        <!-- Continue checkout -->
        <div style="padding:8px 20px 24px;text-align:center;">
          <button id="neodocs-marketplace-continue" style="
            background:none;border:none;
            color:#1a73e8;font-size:15px;font-weight:600;
            cursor:pointer;padding:10px 20px;
          ">Continue Checkout</button>
        </div>
      </div>

      <style>
        @keyframes neodocs-slide-up {
          from { transform:translateY(16px); opacity:0; }
          to   { transform:translateY(0);    opacity:1; }
        }
        #neodocs-marketplace-modal a:hover { opacity:0.88; }
        #neodocs-marketplace-close:hover { background:#e0e0e0 !important; }
        #neodocs-marketplace-continue:hover { text-decoration:underline; }
      </style>
    `;

    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => { modal.style.opacity = '1'; });

    const close = () => {
      cleanupModalHistory();
      modal.style.opacity = '0';
      setTimeout(() => { modal.remove(); document.body.style.overflow = ''; }, 300);
    };

    // Intercept mobile back button
    pushModalToHistory(close);

    modal.querySelector('#neodocs-marketplace-close').addEventListener('click', close);
    modal.querySelector('#neodocs-marketplace-continue').addEventListener('click', () => {
      close();
      setTimeout(() => handleCartCheckout({}), 300);
    });
    modal.addEventListener('click', e => { if (e.target === modal) close(); });

    const escHandler = e => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', escHandler); } };
    document.addEventListener('keydown', escHandler);
  }

  /**
   * Close abandoned checkout modal
   */
  function closeAbandonedModal() {
    cleanupModalHistory();
    const modal = document.getElementById('neodocs-abandoned-modal');
    if (modal) {
      modal.style.opacity = '0';
      setTimeout(() => {
        modal.remove();
        document.body.style.overflow = '';
      }, 300);
    }
  }

  /**
   * Close checkout iframe modal
   * @param {boolean} skipAbandonedHandling - If true, don't trigger abandoned checkout flow
   */
  function closeCheckoutIframe(skipAbandonedHandling = false) {
    const wrapper = document.getElementById(WRAPPER_ID);
    const iframe = document.getElementById(IFRAME_ID);
    const loadingDiv = document.getElementById(LOADING_ID);

    // Clear any progress animation
    if (loadingDiv && loadingDiv.progressInterval) {
      clearInterval(loadingDiv.progressInterval);
    }

    // Remove message listener
    if (iframe && iframe.messageHandler) {
      window.removeEventListener('message', iframe.messageHandler);
    }

    // Remove cart message listener
    if (iframe && iframe.cartMessageHandler) {
      window.removeEventListener('message', iframe.cartMessageHandler);
    }

    // Remove escape key listener
    document.removeEventListener('keydown', handleEscapeKey);

    // Clean up history state pushed for mobile back button
    cleanupModalHistory();

    // Restore body scroll
    document.body.style.overflow = '';

    if (wrapper) {
      wrapper.style.opacity = '0';
      setTimeout(() => wrapper.remove(), 200);
    }

    // Check if this was an abandoned checkout (not successful and not skipped)
    if (!skipAbandonedHandling && checkoutState.isOpen && !checkoutState.checkoutSuccessful) {
      // Reset state before showing abandoned modal
      const abandonedCartData = { ...checkoutState };
      resetCheckoutState();

      // Slight delay to let the iframe close first
      setTimeout(() => {
        handleAbandonedCheckout('manual_close', abandonedCartData);
      }, 250);
    } else {
      resetCheckoutState();
    }
  }

  /**
   * Reset checkout state
   */
  function resetCheckoutState() {
    checkoutState = {
      isOpen: false,
      startTime: null,
      cartData: null,
      checkoutSuccessful: false
    };
  }

  /**
   * Update loading state with error
   * @param {string} message - User-facing message
   * @param {*} [error] - Underlying error to surface for diagnosis
   */
  function updateLoadingError(message, error) {
    const loadingDiv = document.getElementById(LOADING_ID);
    if (!loadingDiv) return;

    const escapeHtml = (str) => String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    let detail = '';
    if (error != null) {
      const text = error instanceof Error ? error.message : String(error);
      if (text) {
        detail = `<p style="margin: 0 0 15px; font-size: 12px; color: #888; word-break: break-word;">${escapeHtml(text)}</p>`;
      }
    }

    loadingDiv.innerHTML = `
      <div class="neodocs-loading-content">
        <p style="color: #dc3545; margin-bottom: 8px;">${message}</p>
        ${detail}
        <button onclick="document.getElementById('${WRAPPER_ID}').remove(); document.body.style.overflow='';"
                style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px;">
          Close
        </button>
      </div>
    `;
  }

  /**
   * Set button loading state
   */
  function setLoading(buttonEl) {
    if (!buttonEl) return;
    buttonEl.disabled = true;
    buttonEl.setAttribute("data-original-text", buttonEl.textContent);
    buttonEl.textContent = "Processing...";
  }

  /**
   * Reset button state
   */
  function resetButton(buttonEl) {
    if (!buttonEl) return;
    buttonEl.disabled = false;
    const original = buttonEl.getAttribute("data-original-text");
    if (original) buttonEl.textContent = original;
  }

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  /**
   * Handle Buy Now button click
   */
  function handleFloBuyNowBtn(e) {
    e.preventDefault();

    const target = e.target.closest('button') || e.target;
    if (target.disabled) return;

    setLoading(target);
    showCheckoutIframe();
    fetchAndPrepareCartData(loadCheckoutWithCartData, target);
  }

  /**
   * Handle Cart Page checkout button click
   */
  function handleCartCheckout(e) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const target = e?.target?.closest('button') || e?.target;
    if (target?.disabled) return;

    if (target) setLoading(target);
    showCheckoutIframe();
    fetchExistingCartData(loadCheckoutWithCartData, target);
  }

  /**
   * Handle successful Add to Cart - opens iframe cart view
   */
  function handleFloCartBtn(event) {
    console.log('[NeoDocs] Opening iframe cart after add-to-cart');
    showCartIframe();
  }

  /**
   * Show the cart iframe modal
   */
  function showCartIframe() {
    // Create modal wrapper (reuse checkout modal styling)
    const existingWrapper = document.getElementById(WRAPPER_ID);
    if (existingWrapper) existingWrapper.remove();

    const wrapper = document.createElement('div');
    wrapper.id = WRAPPER_ID;
    wrapper.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.5);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;

    const iframeContainer = document.createElement('div');
    iframeContainer.style.cssText = `
      position: relative;
      width: 100%;
      max-width: 500px;
      height: 90vh;
      max-height: 700px;
      background: white;
      border-radius: 16px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.3);
      overflow: hidden;
    `;

    // Create loading state with enhanced animation
    const loadingDiv = createLoadingAnimation('cart');
    iframeContainer.appendChild(loadingDiv);

    // Create iframe
    const iframe = document.createElement('iframe');
    iframe.id = IFRAME_ID;
    iframe.src = CART_IFRAME_URL;
    iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    iframe.setAttribute('loading', 'eager');
    iframe.setAttribute('fetchpriority', 'high');

    iframeContainer.appendChild(iframe);
    wrapper.appendChild(iframeContainer);
    document.body.appendChild(wrapper);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Fade in
    requestAnimationFrame(() => {
      wrapper.style.opacity = '1';
    });

    // Close on backdrop click
    wrapper.addEventListener('click', (e) => {
      if (e.target === wrapper) closeCheckoutIframe();
    });

    // Close on Escape key
    document.addEventListener('keydown', handleEscapeKey);

    // Setup message handler for cart operations
    setupCartMessageHandler(iframe);

    // Start fetching cart data immediately (parallel with iframe load)
    let cartDataReady = null;
    let iframeLoaded = false;

    const showCartIframeContent = () => {
      if (cartDataReady && iframeLoaded) {
        sendCartDataToIframe(cartDataReady, iframe);
        // Clear progress animation
        if (loadingDiv.progressInterval) {
          clearInterval(loadingDiv.progressInterval);
        }
        // Smooth transition
        loadingDiv.style.opacity = '0';
        loadingDiv.style.transition = 'opacity 0.2s ease';
        iframe.style.opacity = '1';
        setTimeout(() => {
          if (loadingDiv.parentNode) loadingDiv.remove();
        }, 200);
      }
    };

    // Fetch cart data immediately
    fetchExistingCartData((cartData) => {
      cartDataReady = cartData;
      showCartIframeContent();
    });

    // Handle iframe load
    iframe.addEventListener('load', () => {
      iframeLoaded = true;
      showCartIframeContent();
    });
  }

  /**
   * Setup message handler for cart operations from iframe
   */
  function setupCartMessageHandler(iframe) {
    const handleCartMessage = async (event) => {
      if (event.origin !== CHECKOUT_ORIGIN) return;

      console.log('[NeoDocs] Cart message received:', event.data?.type);

      switch (event.data?.type) {
        case 'CART_READY':
          // Iframe signals it's ready for cart data
          console.log('[NeoDocs] Cart iframe ready');
          break;

        case 'CART_UPDATE_QUANTITY':
          // Update item quantity via Shopify API
          await updateCartItemQuantity(event.data.lineId, event.data.quantity, iframe);
          break;

        case 'CART_REMOVE_ITEM':
          // Remove item (quantity = 0)
          await updateCartItemQuantity(event.data.lineId, 0, iframe);
          break;

        case 'CART_APPLY_COUPON':
          // Apply discount code
          await applyCartDiscountCode(event.data.code, iframe);
          break;

        case 'CART_CLOSE':
          // User clicked continue shopping
          closeCheckoutIframe();
          break;

        case 'NAVIGATE_TO_CHECKOUT':
          // User proceeding to checkout - iframe handles internal navigation
          console.log('[NeoDocs] User navigating to checkout');
          break;

        case 'CHECKOUT_READY':
          // Checkout view is ready
          console.log('[NeoDocs] Checkout view ready in iframe');
          break;

        case 'FLO_CHECKOUT_CLOSE':
          if (event.data.statusType === 'success') {
            checkoutState.checkoutSuccessful = true;
            handleCheckoutSuccess(event.data);
          } else if (event.data.statusType === 'abandon') {
            // Explicit abandon from iframe - just redirect, no modal
            handleExplicitAbandon();
          } else {
            // Back button case (statusType is empty/undefined) - show feedback modal
            handleAbandonedCheckout('back_button', event.data);
          }
          break;
      }
    };

    window.addEventListener('message', handleCartMessage);
    iframe.cartMessageHandler = handleCartMessage;
  }

  /**
   * Update cart item quantity via Shopify API and send updated cart to iframe
   */
  async function updateCartItemQuantity(lineId, quantity, iframe) {
    console.log('[NeoDocs] Updating cart item:', lineId, 'quantity:', quantity);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineId, quantity: quantity })
      });

      const cart = await response.json();
      console.log('[NeoDocs] Cart updated:', cart);

      // Send updated cart to iframe
      const cartData = buildCartPayload(cart);
      sendCartUpdateToIframe(cartData, iframe);

      // Also update cart badge in theme
      updateCartBadge(cart.item_count);

    } catch (error) {
      console.error('[NeoDocs] Error updating cart:', error);
      // Send error to iframe
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'CART_ERROR',
          error: 'Failed to update cart. Please try again.'
        }, CHECKOUT_ORIGIN);
      }
    }
  }

  /**
   * Apply discount code via Shopify and send updated cart to iframe
   */
  async function applyCartDiscountCode(code, iframe) {
    console.log('[NeoDocs] Applying discount code:', code);

    try {
      // Shopify discount URL pattern
      await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET' });

      // Fetch updated cart with discount applied
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const cartData = buildCartPayload(cart);
      sendCartUpdateToIframe(cartData, iframe);

    } catch (error) {
      console.error('[NeoDocs] Error applying discount:', error);
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'CART_ERROR',
          error: 'Failed to apply discount code.'
        }, CHECKOUT_ORIGIN);
      }
    }
  }

  /**
   * Send cart data to iframe
   */
  function sendCartDataToIframe(cartData, iframe) {
    if (iframe?.contentWindow) {
      console.log('[NeoDocs] Sending cart data to iframe:', cartData);
      iframe.contentWindow.postMessage({
        type: 'SHOPIFY_CART_DATA',
        cart: cartData,
        cookies: document.cookie
      }, CHECKOUT_ORIGIN);
    }
  }

  /**
   * Send cart update to iframe after modification
   */
  function sendCartUpdateToIframe(cartData, iframe) {
    if (iframe?.contentWindow) {
      console.log('[NeoDocs] Sending cart update to iframe:', cartData);
      iframe.contentWindow.postMessage({
        type: 'CART_UPDATED',
        cart: cartData
      }, CHECKOUT_ORIGIN);
    }
  }

  /**
   * Update cart badge count in theme header
   */
  function updateCartBadge(count) {
    const cartCountBubble = document.querySelector('.cart-count-bubble');
    if (cartCountBubble) {
      const countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
      if (countSpan) countSpan.textContent = count;

      // Show/hide bubble based on count
      if (count === 0) {
        cartCountBubble.classList.add('hidden');
      } else {
        cartCountBubble.classList.remove('hidden');
      }
    }
  }

  /**
   * Handle checkout button click from cart drawer/notification/page
   * Alias for handleCartCheckout, exposed for onclick handlers
   */
  function handleFloCheckoutBtn(event) {
    console.log('[NeoDocs] Checkout button clicked from cart');
    handleCartCheckout(event || {});
  }

  /**
   * Handle click events via delegation
   */
  function handleDocumentClick(e) {
    const target = e.target;

    // Buy Now button (by class or id)
    if (target.classList.contains('flo-buy-now-button') ||
      target.id === 'flo-buy-now-button' ||
      target.classList.contains('neodocs-buy-now')) {
      handleFloBuyNowBtn(e);
      return;
    }

    // Cart checkout button
    if (target.classList.contains('neodocs-cart-checkout') ||
      target.id === 'neodocs-cart-checkout') {
      handleCartCheckout(e);
      return;
    }
  }

  /**
   * Bind checkout buttons via event delegation
   */
  function bindCheckoutButtons() {
    document.removeEventListener('click', handleDocumentClick);
    document.addEventListener('click', handleDocumentClick);

    // Also hijack the default cart checkout button if configured
    hijackCartCheckoutButton();
  }

  /**
   * Hijack default Shopify cart checkout button
   */
  function hijackCartCheckoutButton() {
    // Look for common cart checkout button selectors
    const checkoutButtons = document.querySelectorAll([
      'form[action="/cart"] button[type="submit"][name="checkout"]',
      'form[action="/cart"] input[type="submit"][name="checkout"]',
      '.cart__checkout-button',
      '#checkout',
      'button[name="checkout"]'
    ].join(','));

    checkoutButtons.forEach(button => {
      if (button.hasAttribute('data-neodocs-bound')) return;
      button.setAttribute('data-neodocs-bound', 'true');

      button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        handleCartCheckout(e);
      });
    });
  }

  // ==========================================
  // ORDER STATUS PAGE
  // ==========================================

  /**
   * Read the order token from the current URL.
   * Supports both `/pages/order-status/<token>` (path segment) and `?token=<token>` (query).
   */
  function ndReadTokenFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get('token') || params.get('order_token') || params.get('order');
      if (q && q.trim()) return q.trim();
      const m = window.location.pathname.match(/\/pages\/order-status\/([^\/?#]+)/i);
      if (m && m[1]) return decodeURIComponent(m[1]).trim();
    } catch (e) {
      console.warn('[NeoDocs] Could not read token from URL:', e);
    }
    return null;
  }

  /**
   * Initialize order status page if on that route
   */
  function initOrderStatusPage() {
    if (!window.location.pathname.includes('/pages/order-status')) {
      return;
    }

    console.log('[NeoDocs] Initializing order status page');

    // Token can arrive as a path segment (/pages/order-status/<token>) or a query param
    // (?token=<token>). Prefer whatever the URL carries so a directly-opened / shared /
    // bookmarked link resolves to that exact order; fall back to the last order on this device.
    const urlToken = ndReadTokenFromUrl();
    const token = urlToken || localStorage.getItem('neodocs_last_order_token');

    // Inject the order-status styles and turn the page into an immersive confirmation screen
    injectOrderStatusStyles();
    document.body.classList.add('neodocs-order-status-page');

    // Render into the theme's real main region. Resolve in priority order — a single
    // querySelector list would match the header's .shopify-section first (it's earlier in the DOM),
    // replacing the header and leaving the theme's "Order Status" page title behind.
    const mainContent =
      document.getElementById('MainContent') ||
      document.querySelector('main') ||
      document.querySelector('.page-content') ||
      document.querySelector('.shopify-section');
    if (mainContent) {
      mainContent.innerHTML = createOrderStatusHTML();
      setTimeout(() => loadOrderDetails(token), 100);
    }
  }

  function createOrderStatusHTML() {
    return `
      <div class="nd-os">
        <div id="neodocs-order-content" class="nd-os-card">
          <div class="nd-os-loading">
            <div class="nd-os-spinner" aria-hidden="true"></div>
            <p class="nd-os-loading-title">Loading your order…</p>
            <p class="nd-os-loading-sub">Fetching your confirmation details</p>
            <div class="nd-os-skel nd-os-skel-hero"></div>
            <div class="nd-os-skel-row">
              <div class="nd-os-skel nd-os-skel-thumb"></div>
              <div class="nd-os-skel-lines">
                <div class="nd-os-skel nd-os-skel-line"></div>
                <div class="nd-os-skel nd-os-skel-line short"></div>
              </div>
            </div>
            <div class="nd-os-skel-row">
              <div class="nd-os-skel nd-os-skel-thumb"></div>
              <div class="nd-os-skel-lines">
                <div class="nd-os-skel nd-os-skel-line"></div>
                <div class="nd-os-skel nd-os-skel-line short"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async function loadOrderDetails(token) {
    const contentDiv = document.getElementById('neodocs-order-content');
    if (!contentDiv) return;

    // 1) Instant first paint from the checkout's cached snapshot — but ONLY when it belongs to
    //    the order being requested. If the URL token points at a different (shared / bookmarked)
    //    order, skip the stale snapshot and let the API be the single source of truth.
    let paintedFromCache = false;
    try {
      const cachedOrder = localStorage.getItem('neodocs_order_details');
      const cachedAddress = localStorage.getItem('neodocs_user_address');
      if (cachedOrder) {
        const orderDetails = JSON.parse(cachedOrder);
        if (ndTokenMatches(orderDetails, token)) {
          const userAddress = cachedAddress ? JSON.parse(cachedAddress) : null;
          displayOrderDetails(contentDiv, orderDetails, userAddress);
          paintedFromCache = true;
        } else {
          console.log('[NeoDocs] Cached snapshot is for a different order — loading fresh from API.');
        }
        // Consume the one-shot snapshot either way, so a later direct open always hits the API.
        localStorage.removeItem('neodocs_order_details');
        localStorage.removeItem('neodocs_user_address');
      }
    } catch (e) {
      console.warn('[NeoDocs] Could not read cached order snapshot:', e);
    }

    // 2) Authoritative fetch: hand the id to the backend and render whatever it returns.
    //    This is where the real Shopify order number + full, up-to-date details come from.
    if (!token) {
      if (!paintedFromCache) displayOrderError(contentDiv, 'No order reference provided.');
      return;
    }

    try {
      const response = await fetch(`${ORDER_STATUS_API}/${encodeURIComponent(token)}`);
      const data = await response.json();
      console.log('[NeoDocs] order-by-token response:', data);

      if (data && data.success && data.order) {
        // API wins — replace the cached first paint with authoritative data.
        displayOrderDetails(contentDiv, data.order, data.user);
      } else if (!paintedFromCache) {
        displayOrderError(contentDiv, 'Order not found.');
      }
    } catch (error) {
      console.error('[NeoDocs] Error loading order from API:', error);
      // Keep the cached render if we already have one; only error out if the screen is empty.
      if (!paintedFromCache) displayOrderError(contentDiv, 'Failed to load order details.');
    }
  }

  function displayOrderDetails(container, order, user) {
    order = order || {};
    const items = Array.isArray(order.items) ? order.items : [];
    const currency = order.currency || 'INR';

    // Resolve delivery address — the API returns it as order.shipping_address (a single object).
    // Fall back to a user object with addresses[] or a flat address for older/cached payloads.
    const address =
      (order.shipping_address && typeof order.shipping_address === 'object' ? order.shipping_address : null) ||
      (user && Array.isArray(user.addresses) && user.addresses.length ? user.addresses[user.addresses.length - 1] : null) ||
      (user && (user.address || user.city || user.pincode) ? user : null);

    // Greeting name
    const rawName =
      (address && (address.name || address.first_name)) ||
      (user && (user.name || user.first_name)) || '';
    const firstName = String(rawName).trim().split(/\s+/)[0] || '';

    // Line + order totals (prices arrive in rupees; fall back to summing line items)
    const lineTotal = (it) => {
      const lp = Number(it.linePrice);
      if (isFinite(lp) && lp > 0) return lp;
      return (Number(it.price) || 0) * (Number(it.quantity) || 1);
    };
    const computedSubtotal = items.reduce((s, it) => s + lineTotal(it), 0);
    const subtotal = Number(order.itemsSubtotalPrice != null ? order.itemsSubtotalPrice : (order.subtotal != null ? order.subtotal : computedSubtotal)) || computedSubtotal;
    const discount = Number(order.totalDiscount) || 0;
    const total = Number(order.totalPrice != null ? order.totalPrice : (subtotal - discount));
    const grandTotal = isFinite(total) ? total : Math.max(0, subtotal - discount);
    const itemCount = Number(order.itemCount) || items.reduce((s, it) => s + (Number(it.quantity) || 1), 0);

    // Payment badge — combines payment_status + payment_method (e.g. pending + cod → Cash on Delivery)
    const paid = String(order.payment_status || '').toLowerCase() === 'paid';
    const method = String(order.payment_method || order.paymentMethod || '').toLowerCase();
    const isCOD = method.indexOf('cod') !== -1 || method.indexOf('cash') !== -1;
    let payLabel, payClass;
    if (paid) { payLabel = 'Paid'; payClass = 'nd-os-pay--paid'; }
    else if (isCOD) { payLabel = 'Cash on Delivery'; payClass = 'nd-os-pay--cod'; }
    else { payLabel = 'Payment pending'; payClass = 'nd-os-pay--pending'; }
    const payMethodLabel = ndPaymentMethodLabel(method, isCOD, paid);
    const totalLabel = paid ? 'Total paid' : (isCOD ? 'Amount due (COD)' : 'Total');

    // For online (prepaid) orders, surface the payment/transaction id so customers can quote it.
    const paymentId = paid && !isCOD
      ? (order.razorpay_payment_id || order.payment_id || order.transaction_id || order.razorpayPaymentId || null)
      : null;

    // Real Shopify order identity from the backend; fall back to the correlation token.
    const shopifyName = order.shopify_order_name || order.order_name || order.name || null;
    const shopifyNumber = (order.shopify_order_number != null) ? order.shopify_order_number
      : (order.order_number != null ? order.order_number : null);
    const orderNumberDisplay =
      (shopifyName != null && String(shopifyName).trim() !== '') ? String(shopifyName).trim()
      : (shopifyNumber != null && String(shopifyNumber).trim() !== '' ? '#' + String(shopifyNumber) : null);
    const idLabel = orderNumberDisplay ? 'Order number' : 'Reference ID';
    const idValue = orderNumberDisplay || String(order.token || order.id || '—');

    // Order status → progress tracker + estimated delivery (measured from when it was placed)
    const status = order.status || '';
    const isDelivered = String(status).toLowerCase().indexOf('deliver') !== -1;
    // Created date arrives in different shapes across order types: a Firestore {_seconds} object,
    // an ISO string, or epoch seconds/ms. ndToMs() normalizes all of them.
    const createdMs = [order.shopify_order_created_at, order.created_at, order.createdAt, order.timestamp]
      .map(ndToMs).find(function (x) { return isFinite(x); });
    const placedOn = ndFormatDate(createdMs);

    // Items list
    const itemsHtml = items.length ? items.map((item, i) => {
      const title = ndEscapeHtml(item.title || 'Item');
      const variantRaw = (item.variant_title && item.variant_title !== 'Default Title')
        ? item.variant_title
        : ((item.variant && item.variant !== 'Default Title') ? item.variant : '');
      const qty = Number(item.quantity) || 1;
      const img = item.image ? String(item.image) : '';
      const unit = qty > 1 ? `<span class="nd-os-item-unit">${ndMoney(item.price, currency)} each</span>` : '';
      return `
        <div class="nd-os-item" style="animation-delay:${(0.04 * i).toFixed(2)}s">
          <div class="nd-os-item-thumb">
            <span class="nd-os-thumb-fallback">🧬</span>
            ${img ? `<img src="${ndEscapeHtml(img)}" alt="" loading="lazy" onerror="this.remove();">` : ''}
            <span class="nd-os-item-qtybubble">${qty}</span>
          </div>
          <div class="nd-os-item-info">
            <div class="nd-os-item-title">${title}</div>
            ${variantRaw ? `<div class="nd-os-item-variant">${ndEscapeHtml(variantRaw)}</div>` : ''}
            <div class="nd-os-item-qty">Qty ${qty}</div>
          </div>
          <div class="nd-os-item-price">${ndMoney(lineTotal(item), currency)}${unit}</div>
        </div>`;
    }).join('') : `<div class="nd-os-item-empty">Your order details will appear here shortly.</div>`;

    // Order summary
    const summaryHtml = `
      <div class="nd-os-summary">
        <div class="nd-os-sum-row"><span>Subtotal (${itemCount} item${itemCount === 1 ? '' : 's'})</span><span>${ndMoney(subtotal, currency)}</span></div>
        ${discount > 0 ? `<div class="nd-os-sum-row nd-os-sum-disc"><span>Discount</span><span>−${ndMoney(discount, currency)}</span></div>` : ''}
        <div class="nd-os-sum-row"><span>Shipping</span><span class="nd-os-free">Free</span></div>
        <div class="nd-os-sum-row"><span>Payment</span><span>${ndEscapeHtml(payMethodLabel)}</span></div>
        ${paymentId ? `<div class="nd-os-sum-row"><span>Payment ID</span><span class="nd-os-mono">${ndEscapeHtml(paymentId)}</span></div>` : ''}
        <div class="nd-os-sum-total"><span>${totalLabel}</span><span>${ndMoney(grandTotal, currency)}</span></div>
      </div>`;

    // Delivery address
    const cityLine = [address && address.city, address && address.state].filter(Boolean).map(ndEscapeHtml).join(', ');
    const addressHtml = address ? `
      <section class="nd-os-section nd-os-reveal" style="animation-delay:.28s">
        <h2 class="nd-os-h2">${ndPinIcon()} Delivery address</h2>
        <div class="nd-os-address">
          ${(address.name || firstName) ? `<div class="nd-os-addr-name">${ndEscapeHtml(address.name || firstName)}</div>` : ''}
          ${address.address ? `<div>${ndEscapeHtml(address.address)}</div>` : ''}
          ${cityLine || address.pincode ? `<div>${cityLine}${address.pincode ? (cityLine ? ' - ' : '') + ndEscapeHtml(address.pincode) : ''}</div>` : ''}
          ${address.phone ? `<div class="nd-os-addr-phone">${ndPhoneIcon()} ${ndEscapeHtml(address.phone)}</div>` : ''}
        </div>
      </section>` : '';

    container.innerHTML = `
      <div class="nd-os-hero">
        <div class="nd-os-badge" aria-hidden="true">
          <svg viewBox="0 0 60 60" width="82" height="82">
            <circle class="nd-os-ring" cx="30" cy="30" r="26" fill="none" stroke="#22a06b" stroke-width="4" stroke-linecap="round"/>
            <path class="nd-os-check" d="M19 30 L27 38 L42 22" fill="none" stroke="#22a06b" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1 class="nd-os-title">Order confirmed${firstName ? ', ' + ndEscapeHtml(firstName) : ''}! 🎉</h1>
        <p class="nd-os-sub">Thank you — we've received your order and our team is on it.</p>
        <div class="nd-os-metarow">
          <span class="nd-os-chip ${payClass}">${payLabel}</span>
        </div>
        ${placedOn ? `<p class="nd-os-placed">Placed on ${ndEscapeHtml(placedOn)}</p>` : ''}
      </div>

      <div class="nd-os-idcard nd-os-reveal" style="animation-delay:.04s">
        <div class="nd-os-idcard-main">
          <span class="nd-os-idcard-label">${idLabel}</span>
          <span class="nd-os-idcard-value">${ndEscapeHtml(idValue)}</span>
        </div>
        <button type="button" class="nd-os-copy" data-copy="${ndEscapeHtml(idValue)}" onclick="try{navigator.clipboard.writeText(this.dataset.copy);var t=this.textContent;this.textContent='Copied ✓';var b=this;setTimeout(function(){b.textContent=t;},1500);}catch(e){}">Copy</button>
      </div>

      ${isDelivered ? `
      <div class="nd-os-eta nd-os-eta--done nd-os-reveal" style="animation-delay:.06s">
        ${ndTruckIcon()}
        <div>
          <span class="nd-os-eta-label">Status</span>
          <span class="nd-os-eta-date">Delivered ✓</span>
        </div>
      </div>` : `
      <div class="nd-os-eta nd-os-reveal" style="animation-delay:.06s">
        ${ndTruckIcon()}
        <div>
          <span class="nd-os-eta-label">Estimated delivery</span>
          <span class="nd-os-eta-date">${ndDeliveryWindow(createdMs)}</span>
        </div>
      </div>`}

      ${ndOrderProgress(status)}

      <section class="nd-os-section nd-os-reveal" style="animation-delay:.16s">
        <h2 class="nd-os-h2">${ndBagIcon()} Your items <span class="nd-os-count">${itemCount}</span></h2>
        <div class="nd-os-items">${itemsHtml}</div>
      </section>

      <section class="nd-os-section nd-os-reveal" style="animation-delay:.22s">
        ${summaryHtml}
      </section>

      ${addressHtml}

      <div class="nd-os-trust-wrap nd-os-reveal" style="animation-delay:.34s">
        ${ndOrderTrustRow()}
      </div>

      <div class="nd-os-support nd-os-reveal" style="animation-delay:.36s">
        <div class="nd-os-support-text">
          <span class="nd-os-support-title">Need help with your order?</span>
          <span class="nd-os-support-sub">Our care team is available 7 days a week</span>
        </div>
        <a href="tel:+918976787378" class="nd-os-support-call">${ndPhoneIcon()} 8976787378</a>
      </div>

      <div class="nd-os-actions nd-os-reveal" style="animation-delay:.42s">
        <a href="/" class="nd-os-btn nd-os-btn--primary">Continue shopping</a>
      </div>

      <p class="nd-os-footnote">A confirmation has been sent to you.</p>
    `;
  }

  function displayOrderError(container, message) {
    container.innerHTML = `
      <div class="nd-os-hero">
        <div class="nd-os-badge nd-os-badge--warn" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#c47f00" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <h1 class="nd-os-title">We couldn't load your order</h1>
        <p class="nd-os-sub">${ndEscapeHtml(message)}</p>
      </div>
      <div class="nd-os-trust-wrap">${ndOrderTrustRow()}</div>
      <div class="nd-os-actions">
        <a href="/" class="nd-os-btn nd-os-btn--primary">Back to store</a>
        <a href="tel:+918976787378" class="nd-os-btn nd-os-btn--ghost">${ndPhoneIcon()} Call us: 8976787378</a>
      </div>
    `;
  }

  // ---- Order status helpers ----------------------------------------------

  function ndEscapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function ndCurrencySymbol(cur) {
    const map = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED ', AUD: 'A$', CAD: 'C$', SGD: 'S$' };
    return map[String(cur || 'INR').toUpperCase()];
  }

  function ndMoney(amount, currency) {
    const sym = ndCurrencySymbol(currency);
    const n = Number(amount);
    if (!isFinite(n)) return (sym || (String(currency || '') + ' ')) + (amount != null ? amount : '0');
    const formatted = n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return sym ? sym + formatted : (String(currency || '').toUpperCase() + ' ' + formatted);
  }

  // Does the cached order snapshot belong to the requested token? Lenient by design: only returns
  // false on a clear mismatch (snapshot carries token(s) and none relate to the requested one),
  // so the post-checkout instant paint is preserved while a stale/other order is never shown.
  function ndTokenMatches(order, token) {
    if (!token) return true;
    if (!order) return false;
    const norm = (v) => String(v == null ? '' : v).replace(/[^a-z0-9]/gi, '').toLowerCase();
    const t = norm(token);
    if (!t) return true;
    const candidates = [order.token, order.normalizedToken, order.rawToken, order.id].map(norm).filter(Boolean);
    if (!candidates.length) return true; // nothing to compare against → don't block the paint
    return candidates.some((c) => c === t || c.indexOf(t) !== -1 || t.indexOf(c) !== -1);
  }

  // Normalize a timestamp of unknown shape (Firestore {_seconds}, ISO string, epoch s or ms) to ms.
  function ndToMs(v) {
    if (v == null) return NaN;
    if (typeof v === 'number') return v > 1e12 ? v : v * 1000;      // ms vs seconds
    if (typeof v === 'string') { const p = Date.parse(v); return isFinite(p) ? p : NaN; }
    if (typeof v === 'object' && v._seconds != null) return v._seconds * 1000;
    return NaN;
  }

  function ndPaymentMethodLabel(method, isCOD, paid) {
    const m = String(method || '').toLowerCase();
    if (isCOD) return 'Cash on Delivery';
    if (m.indexOf('card') !== -1) return 'Card';
    if (m.indexOf('upi') !== -1) return 'UPI';
    if (m.indexOf('netbank') !== -1 || m.indexOf('bank') !== -1) return 'Net Banking';
    if (m.indexOf('wallet') !== -1) return 'Wallet';
    if (m) return method.charAt(0).toUpperCase() + method.slice(1);
    return paid ? 'Paid online' : '—';
  }

  function ndFormatDate(ms) {
    try {
      if (!isFinite(ms)) return '';
      return new Date(ms).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  function ndDeliveryWindow(baseMs) {
    try {
      const base = (typeof baseMs === 'number' && isFinite(baseMs)) ? new Date(baseMs) : new Date();
      const fmt = (d) => d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
      const start = new Date(base); start.setDate(start.getDate() + 3);
      const end = new Date(base); end.setDate(end.getDate() + 6);
      return fmt(start) + ' – ' + fmt(end);
    } catch (e) {
      return 'in 3–6 days';
    }
  }

  // Map the backend order status onto the 4-step tracker. Once an order exists, "Confirmed"
  // is always complete; unknown/pending statuses sit at "Processing".
  function ndOrderProgress(status) {
    const s = String(status || '').toLowerCase();
    const steps = ['Confirmed', 'Processing', 'Shipped', 'Delivered'];
    let active = 1;        // 0-based index of the current step
    let allDone = false;
    if (s.indexOf('deliver') !== -1) { active = 3; allDone = true; }
    else if (s.indexOf('out for') !== -1 || s.indexOf('ship') !== -1 || s.indexOf('transit') !== -1 || s.indexOf('dispatch') !== -1) active = 2;
    else active = 1;       // pending / confirmed / processing / placed / paid / unknown

    let html = '<div class="nd-os-track nd-os-reveal" style="animation-delay:.1s">';
    steps.forEach((label, i) => {
      const state = allDone ? 'done' : (i < active ? 'done' : (i === active ? 'active' : ''));
      if (i > 0) {
        const prevDone = allDone || (i - 1) < active;
        html += `<div class="nd-os-track-bar ${prevDone ? 'is-done' : ''}"></div>`;
      }
      const dot = state === 'done' ? '✓' : (i + 1);
      html += `
        <div class="nd-os-track-step ${state}">
          <span class="nd-os-track-dot">${dot}</span>
          <span class="nd-os-track-label">${label}</span>
        </div>`;
    });
    html += '</div>';
    return html;
  }

  function ndOrderTrustRow() {
    const L = window.NEODOCS_TRUST_LOGOS || {};
    const badge = (logo, label) => `
      <div class="nd-os-trust-item">
        ${logo ? `<img src="${ndEscapeHtml(logo)}" alt="${ndEscapeHtml(label)}" class="nd-os-trust-logo" onerror="this.style.display='none';this.parentNode.classList.add('is-textonly');">` : ''}
        <span>${ndEscapeHtml(label)}</span>
      </div>`;
    return `
      <div class="nd-os-trust">
        ${badge(L.iso13485, 'ISO 13485')}
        ${badge(L.icmr, 'ICMR')}
        ${badge(L.cdsco, 'CDSCO')}
      </div>
      <p class="nd-os-trust-note">🔒 Secure &amp; private · Recognized by 30,000+ Doctors</p>`;
  }

  function ndTruckIcon() {
    return '<svg class="nd-os-eta-icon" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#22a06b" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="6" width="13" height="11" rx="1.5"/><path d="M14 9h4l3 3v5h-7z"/><circle cx="6" cy="19" r="1.8"/><circle cx="18" cy="19" r="1.8"/></svg>';
  }
  function ndBagIcon() {
    return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#22a06b" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';
  }
  function ndPinIcon() {
    return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#22a06b" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';
  }
  function ndPhoneIcon() {
    return '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
  }

  function injectOrderStatusStyles() {
    if (document.getElementById('neodocs-order-status-styles')) return;
    const style = document.createElement('style');
    style.id = 'neodocs-order-status-styles';
    style.textContent = `
      body.neodocs-order-status-page {
        background: linear-gradient(180deg, #f4faf8 0%, #eef6f3 100%) !important;
      }
      /* Hide the theme's default page heading/breadcrumb ("Order Status") on this route */
      body.neodocs-order-status-page .main-page-title,
      body.neodocs-order-status-page .page-title,
      body.neodocs-order-status-page .breadcrumb,
      body.neodocs-order-status-page .breadcrumbs { display: none !important; }
      .nd-os {
        max-width: 640px;
        margin: 0 auto;
        padding: 28px 16px 56px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        color: #21312c;
        -webkit-font-smoothing: antialiased;
      }
      .nd-os-card {
        background: #ffffff;
        border-radius: 22px;
        padding: 30px 26px;
        box-shadow: 0 18px 50px rgba(20, 70, 55, 0.10), 0 2px 6px rgba(20, 70, 55, 0.04);
        border: 1px solid #eaf1ee;
        text-align: left;
      }

      /* Reveal animation */
      @keyframes nd-os-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      .nd-os-reveal { opacity: 0; animation: nd-os-rise .5s ease forwards; }

      /* Hero */
      .nd-os-hero { text-align: center; padding: 4px 0 8px; }
      .nd-os-badge {
        position: relative;
        width: 96px; height: 96px;
        margin: 0 auto 14px;
        border-radius: 50%;
        background: radial-gradient(circle at 50% 45%, #e7f7f0 0%, #ddf1e8 100%);
        display: flex; align-items: center; justify-content: center;
      }
      .nd-os-badge::after {
        content: ""; position: absolute; inset: -6px; border-radius: 50%;
        border: 2px solid rgba(34, 160, 107, 0.25);
        animation: nd-os-pulse 2.2s ease-out infinite;
      }
      .nd-os-badge--warn { background: radial-gradient(circle at 50% 45%, #fff3e0 0%, #ffe6c2 100%); }
      .nd-os-badge--warn::after { border-color: rgba(196, 127, 0, 0.25); }
      @keyframes nd-os-pulse { 0% { transform: scale(1); opacity: .7; } 70% { transform: scale(1.18); opacity: 0; } 100% { opacity: 0; } }
      .nd-os-ring { stroke-dasharray: 164; stroke-dashoffset: 164; transform: rotate(-90deg); transform-origin: center; animation: nd-os-draw-ring .9s ease-out forwards; }
      .nd-os-check { stroke-dasharray: 44; stroke-dashoffset: 44; animation: nd-os-draw-check .45s .68s ease forwards; }
      @keyframes nd-os-draw-ring { to { stroke-dashoffset: 0; } }
      @keyframes nd-os-draw-check { to { stroke-dashoffset: 0; } }

      .nd-os-title { margin: 0 0 8px; font-size: 25px; line-height: 1.22; font-weight: 800; letter-spacing: -0.4px; color: #16261f; }
      .nd-os-sub { margin: 0 auto; max-width: 400px; font-size: 15px; line-height: 1.5; color: #56665f; }
      .nd-os-metarow { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-top: 16px; }
      .nd-os-placed { margin: 12px 0 0; font-size: 12.5px; color: #8b9a93; }
      .nd-os-chip { display: inline-flex; align-items: center; gap: 6px; padding: 6px 13px; border-radius: 999px; font-size: 13px; font-weight: 700; letter-spacing: .2px; }
      .nd-os-chip--order { background: #eef6f3; color: #2f7d6b; }
      .nd-os-pay--paid { background: #e6f6ec; color: #1f8a4c; }
      .nd-os-pay--paid::before { content: "●"; font-size: 8px; }
      .nd-os-pay--cod { background: #fff4e2; color: #b9770c; }
      .nd-os-pay--cod::before { content: "●"; font-size: 8px; }
      .nd-os-pay--pending { background: #fdecef; color: #c0355a; }
      .nd-os-pay--pending::before { content: "●"; font-size: 8px; }

      /* Order ID card */
      .nd-os-idcard {
        display: flex; align-items: center; gap: 12px; justify-content: space-between;
        margin-top: 16px; padding: 12px 14px 12px 16px;
        border: 1px solid #e6efeb; border-radius: 14px; background: #f7fbf9;
      }
      .nd-os-idcard-main { min-width: 0; }
      .nd-os-idcard-label { display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .6px; color: #7a8a83; margin-bottom: 3px; }
      .nd-os-idcard-value { display: block; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 13px; font-weight: 600; color: #21312c; word-break: break-all; line-height: 1.4; }
      .nd-os-copy { flex-shrink: 0; border: 1px solid #cfe3da; background: #fff; color: #2f7d6b; font-size: 12.5px; font-weight: 700; padding: 8px 14px; border-radius: 10px; cursor: pointer; transition: background .15s ease; }
      .nd-os-copy:hover { background: #eef6f3; }

      /* Support / call CTA */
      .nd-os-support {
        display: flex; align-items: center; gap: 14px; justify-content: space-between; flex-wrap: wrap;
        margin-top: 26px; padding: 16px 18px; border-radius: 16px;
        background: linear-gradient(120deg, #f1faf6 0%, #e8f5ef 100%); border: 1px solid #d8ece3;
      }
      .nd-os-support-text { display: flex; flex-direction: column; }
      .nd-os-support-title { font-size: 14.5px; font-weight: 800; color: #1c3229; }
      .nd-os-support-sub { font-size: 12.5px; color: #6b8078; margin-top: 2px; }
      .nd-os-support-call {
        display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0;
        padding: 12px 20px; border-radius: 12px; text-decoration: none;
        background: linear-gradient(135deg, #22a06b 0%, #1c8a5c 100%); color: #fff;
        font-size: 17px; font-weight: 800; letter-spacing: .4px;
        box-shadow: 0 6px 16px rgba(34,160,107,.28); transition: transform .15s ease, box-shadow .15s ease;
      }
      .nd-os-support-call:hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(34,160,107,.34); }

      /* ETA banner */
      .nd-os-eta {
        display: flex; align-items: center; gap: 14px;
        margin: 22px 0 6px; padding: 15px 18px;
        background: linear-gradient(120deg, #f1faf6 0%, #e8f5ef 100%);
        border: 1px solid #d8ece3; border-radius: 16px;
      }
      .nd-os-eta--done { background: linear-gradient(120deg, #eaf7f0 0%, #ddf1e8 100%); border-color: #cbe7da; }
      .nd-os-eta-icon { flex-shrink: 0; }
      .nd-os-eta-label { display: block; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .6px; color: #6b8078; }
      .nd-os-eta-date { display: block; font-size: 17px; font-weight: 800; color: #1c3229; margin-top: 2px; }

      /* Progress tracker */
      .nd-os-track { display: flex; align-items: flex-start; justify-content: space-between; margin: 24px 2px 6px; }
      .nd-os-track-step { display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 0 0 auto; width: 64px; text-align: center; }
      .nd-os-track-dot {
        width: 30px; height: 30px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 13px; font-weight: 800;
        background: #eef2f0; color: #9aa8a2; border: 2px solid #eef2f0;
        transition: all .3s ease;
      }
      .nd-os-track-step.done .nd-os-track-dot { background: #22a06b; border-color: #22a06b; color: #fff; }
      .nd-os-track-step.active .nd-os-track-dot { background: #fff; border-color: #22a06b; color: #22a06b; box-shadow: 0 0 0 5px rgba(34,160,107,.15); animation: nd-os-beat 1.8s ease-in-out infinite; }
      @keyframes nd-os-beat { 0%,100% { box-shadow: 0 0 0 5px rgba(34,160,107,.15); } 50% { box-shadow: 0 0 0 8px rgba(34,160,107,.05); } }
      .nd-os-track-label { font-size: 11.5px; font-weight: 600; color: #6b7c75; line-height: 1.25; }
      .nd-os-track-step.done .nd-os-track-label, .nd-os-track-step.active .nd-os-track-label { color: #2c4a3f; }
      .nd-os-track-bar { flex: 1 1 auto; height: 3px; border-radius: 3px; background: #e7ede9; margin-top: 14px; }
      .nd-os-track-bar.is-done { background: #22a06b; }

      /* Sections */
      .nd-os-section { margin-top: 26px; }
      .nd-os-h2 { display: flex; align-items: center; gap: 8px; margin: 0 0 14px; font-size: 15px; font-weight: 800; color: #1c3229; letter-spacing: -0.1px; }
      .nd-os-count { margin-left: auto; background: #eef6f3; color: #2f7d6b; font-size: 12px; font-weight: 800; padding: 2px 10px; border-radius: 999px; }

      /* Items */
      .nd-os-items { display: grid; gap: 10px; }
      .nd-os-item {
        display: flex; align-items: center; gap: 14px;
        padding: 12px; border: 1px solid #edf2f0; border-radius: 14px; background: #fbfdfc;
        opacity: 0; animation: nd-os-rise .45s ease forwards;
      }
      .nd-os-item-thumb {
        position: relative; width: 58px; height: 58px; flex-shrink: 0;
        border-radius: 12px; overflow: hidden; background: linear-gradient(135deg, #eaf7f0, #e0f0e8);
        display: flex; align-items: center; justify-content: center;
      }
      .nd-os-item-thumb img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
      .nd-os-thumb-fallback { font-size: 26px; }
      .nd-os-item-qtybubble {
        position: absolute; top: -6px; right: -6px; z-index: 2;
        min-width: 20px; height: 20px; padding: 0 5px; border-radius: 999px;
        background: #22a06b; color: #fff; font-size: 11px; font-weight: 800;
        display: flex; align-items: center; justify-content: center;
        border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,.15);
      }
      .nd-os-item-info { flex: 1 1 auto; min-width: 0; }
      .nd-os-item-title { font-size: 14.5px; font-weight: 700; color: #21312c; line-height: 1.35; }
      .nd-os-item-variant { font-size: 12.5px; color: #7a8a83; margin-top: 2px; }
      .nd-os-item-qty { font-size: 12px; color: #8b9a93; margin-top: 3px; }
      .nd-os-item-price { text-align: right; font-size: 14.5px; font-weight: 800; color: #1c3229; white-space: nowrap; }
      .nd-os-item-unit { display: block; font-size: 11px; font-weight: 500; color: #9aa8a2; margin-top: 2px; }
      .nd-os-item-empty { padding: 20px; text-align: center; color: #8b9a93; font-size: 14px; border: 1px dashed #dbe6e1; border-radius: 14px; }

      /* Summary */
      .nd-os-summary { border: 1px solid #edf2f0; border-radius: 16px; padding: 16px 18px; background: #fbfdfc; }
      .nd-os-sum-row { display: flex; justify-content: space-between; font-size: 14px; color: #56665f; padding: 5px 0; }
      .nd-os-sum-disc span:last-child { color: #1f8a4c; font-weight: 700; }
      .nd-os-free { color: #1f8a4c; font-weight: 700; }
      .nd-os-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12.5px; color: #3a4a44; word-break: break-all; text-align: right; max-width: 60%; }
      .nd-os-sum-total { display: flex; justify-content: space-between; align-items: baseline; margin-top: 10px; padding-top: 13px; border-top: 1px dashed #dbe6e1; font-weight: 800; color: #16261f; }
      .nd-os-sum-total span:last-child { font-size: 22px; letter-spacing: -0.5px; }

      /* Address */
      .nd-os-address { border: 1px solid #edf2f0; border-left: 4px solid #22a06b; border-radius: 14px; padding: 15px 18px; background: #fbfdfc; font-size: 14px; line-height: 1.6; color: #4b5b57; }
      .nd-os-addr-name { font-weight: 700; color: #21312c; margin-bottom: 2px; }
      .nd-os-addr-phone { margin-top: 8px; color: #4b5b57; }

      /* Trust */
      .nd-os-trust-wrap { margin-top: 26px; padding-top: 22px; border-top: 1px solid #eef3f1; }
      .nd-os-trust { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
      .nd-os-trust-item { display: inline-flex; align-items: center; gap: 7px; padding: 7px 14px; border-radius: 999px; background: #f4f9f7; border: 1px solid #e6efeb; font-size: 12.5px; font-weight: 700; color: #2c3b36; }
      .nd-os-trust-logo { height: 18px; width: auto; display: block; }
      .nd-os-trust-note { text-align: center; margin: 14px 0 0; font-size: 12.5px; color: #7a8a83; }

      /* Actions */
      .nd-os-actions { display: grid; gap: 10px; margin-top: 26px; }
      .nd-os-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 15px 24px; border-radius: 13px; font-size: 15px; font-weight: 700; text-decoration: none; text-align: center; transition: transform .15s ease, box-shadow .15s ease, background .15s ease; }
      .nd-os-btn--primary { background: linear-gradient(135deg, #22a06b 0%, #1c8a5c 100%); color: #fff; box-shadow: 0 8px 20px rgba(34, 160, 107, 0.28); }
      .nd-os-btn--primary:hover { transform: translateY(-1px); box-shadow: 0 12px 26px rgba(34, 160, 107, 0.36); }
      .nd-os-btn--ghost { background: #fff; color: #2f7d6b; border: 1.5px solid #d8ece3; }
      .nd-os-btn--ghost:hover { background: #f4f9f7; }
      .nd-os-footnote { text-align: center; margin: 18px 0 0; font-size: 12.5px; color: #9aa8a2; line-height: 1.5; }

      /* Loading */
      .nd-os-loading { text-align: center; padding: 12px 4px; }
      .nd-os-spinner { width: 44px; height: 44px; margin: 8px auto 18px; border-radius: 50%; border: 3px solid #e6efeb; border-top-color: #22a06b; animation: nd-os-spin .8s linear infinite; }
      @keyframes nd-os-spin { to { transform: rotate(360deg); } }
      .nd-os-loading-title { margin: 0 0 4px; font-size: 17px; font-weight: 800; color: #1c3229; }
      .nd-os-loading-sub { margin: 0 0 22px; font-size: 13.5px; color: #7a8a83; }
      .nd-os-skel { background: linear-gradient(90deg, #edf2f0 25%, #f6faf8 50%, #edf2f0 75%); background-size: 200% 100%; animation: nd-os-shimmer 1.5s infinite; border-radius: 8px; }
      @keyframes nd-os-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      .nd-os-skel-hero { height: 74px; border-radius: 14px; margin-bottom: 16px; }
      .nd-os-skel-row { display: flex; gap: 14px; align-items: center; margin-bottom: 12px; }
      .nd-os-skel-thumb { width: 58px; height: 58px; border-radius: 12px; flex-shrink: 0; }
      .nd-os-skel-lines { flex: 1; }
      .nd-os-skel-line { height: 13px; margin-bottom: 8px; }
      .nd-os-skel-line.short { width: 55%; }

      @media (max-width: 560px) {
        .nd-os { padding: 16px 10px 40px; }
        .nd-os-card { padding: 24px 18px; border-radius: 18px; }
        .nd-os-title { font-size: 22px; }
        .nd-os-track-label { font-size: 10.5px; }
        .nd-os-track-step { width: 56px; }
        .nd-os-sum-total span:last-child { font-size: 20px; }
        .nd-os-support { flex-direction: column; align-items: stretch; text-align: center; }
        .nd-os-support-call { justify-content: center; }
      }
    `;
    document.head.appendChild(style);
  }

  // ==========================================
  // INITIALIZATION
  // ==========================================

  /**
   * Initialize NeoDocs Checkout
   */
  function init() {
    console.log('[NeoDocs] Initializing checkout...');
    bindCheckoutButtons();
    initOrderStatusPage();
    setupPreloading();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => preloadCheckoutIframe(), { timeout: 2000 });
    } else {
      setTimeout(preloadCheckoutIframe, 1500);
    }
  }

  // Expose global functions for button onclick handlers
  window.handleFloBuyNowBtn = handleFloBuyNowBtn;
  window.handleNeoDocsCheckout = handleCartCheckout;
  window.handleFloCartBtn = handleFloCartBtn;
  window.handleFloCheckoutBtn = handleFloCheckoutBtn;

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind on dynamic content changes (for SPAs or AJAX-loaded content)
  const observer = new MutationObserver((mutations) => {
    let shouldRebind = false;
    mutations.forEach(mutation => {
      if (mutation.addedNodes.length > 0) {
        shouldRebind = true;
      }
    });
    if (shouldRebind) {
      hijackCartCheckoutButton();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

})();
